import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { Grid } from '@mantine/core'
import useStyles from './HowToBuild.styles'
import { translate } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  HOW_TO_BUILD_TITLE_HTML,
  SECOND_ARTICLE_IMAGE_JPEG,
  SECOND_ARTICLE_IMAGE_AVIF,
  HOW_TO_BUILD_TITLE_HTML_ES,
  HOW_TO_BUILD_TITLE_HTML_FR,
  HOW_TO_BUILD_TITLE_HTML_KO,
  HOW_TO_BUILD_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/how-to-build-your-perfect-workplace-with-the-best-music-strategy-data'
import { IconExternalLink } from '@tabler/icons'
import {
  DEBUG_MODE,
  MAIN_SITE_ENVIRONMENT_HOST,
  MAIN_SITE_ENVIRONMENT_HOST_ES,
  MAIN_SITE_ENVIRONMENT_HOST_FR,
  MAIN_SITE_ENVIRONMENT_HOST_KO,
} from '../../../../utilities/config'
import {
  HOW_MUSIC_BOOSTS_ARTICLE_ROUTE,
  HOW_MUSIC_BOOSTS_ARTICLE_ROUTE_ES,
  HOW_MUSIC_BOOSTS_ARTICLE_ROUTE_FR,
  HOW_MUSIC_BOOSTS_ARTICLE_ROUTE_KO,
} from '../../../../static-data/how-music-boosts-productivity-data'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../controls/BackToArticle'),
  {
    ssr: true,
  }
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../controls/ReadyToTransform'),
  {
    ssr: true,
  }
)

export default function HowToBuild(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
         (max-width: 430px) 300px, 
         (max-width: 590px) 384px, 
         512px"
        src={avifCompatible ? ARTICLE_IMAGE_AVIF : ARTICLE_IMAGE_JPEG}
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  const secondArticleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
         (max-width: 430px) 300px, 
         (max-width: 590px) 384px, 
         512px"
        src={
          avifCompatible ? SECOND_ARTICLE_IMAGE_AVIF : SECOND_ARTICLE_IMAGE_JPEG
        }
        alt=""
        style={{
          marginTop: '10px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  HOW_TO_BUILD_TITLE_HTML,
                  HOW_TO_BUILD_TITLE_HTML_ES,
                  HOW_TO_BUILD_TITLE_HTML_FR,
                  HOW_TO_BUILD_TITLE_HTML_KO,
                  selectedLanguage
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage
                ),
                HOW_TO_BUILD_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false
              )}
              {articleImage}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Welcome back! In Part 1&nbsp;-{' '}
                  <span
                    className={classes.appRoutingLink}
                    onClick={() => {
                      try {
                        props?.navigateRoute(HOW_MUSIC_BOOSTS_ARTICLE_ROUTE)
                      } catch (e) {
                        if (DEBUG_MODE) console.log(e)
                      }
                    }}
                  >
                    How Music Boosts Productivity by 90% in Business
                  </span>{' '}
                  we explored the fascinating neuroscience behind workplace
                  music&nbsp;— how it triggers dopamine release, reduces
                  cortisol by 61%, and boosts performance by 90%. Now you
                  understand <span className={classes.spanItalic}>why</span>{' '}
                  music works for businesses.
                </>,
                <>
                  ¡Bienvenido de nuevo!&nbsp;En la Parte 1,{' '}
                  <span
                    className={classes.appRoutingLink}
                    onClick={() => {
                      try {
                        props?.navigateRoute(HOW_MUSIC_BOOSTS_ARTICLE_ROUTE_ES)
                      } catch (e) {
                        if (DEBUG_MODE) console.log(e)
                      }
                    }}
                  >
                    Cómo la Música Aumenta la Productividad en un 90% en el
                    Ambito Empresarial
                  </span>
                  , exploramos la fascinante neurociencia detrás de la música en
                  el lugar de trabajo: cómo desencadena la liberación de
                  dopamina, reduce el cortisol en un 61% y aumenta el
                  rendimiento en un 90%. Ahora comprende por qué la música
                  funciona para las empresas.
                </>,
                <>
                  Content de te revoir! Dans la première partie,{' '}
                  <span
                    className={classes.appRoutingLink}
                    onClick={() => {
                      try {
                        props?.navigateRoute(HOW_MUSIC_BOOSTS_ARTICLE_ROUTE_FR)
                      } catch (e) {
                        if (DEBUG_MODE) console.log(e)
                      }
                    }}
                  >
                    Comment la Musique Augmente la Productivité de 90 % en
                    Entreprise
                  </span>
                  , nous avons exploré les fascinantes découvertes des
                  neurosciences concernant la musique au travail&nbsp;: comment
                  elle déclenche la libération de dopamine, réduit le taux de
                  cortisol de 61&nbsp;% et améliore les performances de 90 %.
                  Vous comprenez maintenant pourquoi la musique est bénéfique
                  pour les entreprises.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    다시
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    오신
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    환영합니다!
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    1부
                  </span>
                  ,{' '}
                  <span
                    className={classes.appRoutingLink}
                    onClick={() => {
                      try {
                        props?.navigateRoute(HOW_MUSIC_BOOSTS_ARTICLE_ROUTE_KO)
                      } catch (e) {
                        if (DEBUG_MODE) console.log(e)
                      }
                    }}
                  >
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      업무
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      생산성을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      90%
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      향상시키는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방법
                    </span>
                  </span>
                  ,{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    에서는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    직장
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    내
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    숨겨진
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    흥미로운
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    신경과학적
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    원리를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    살펴보았습니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    도파민
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분비를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    촉진하고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    코르티솔
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수치를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    61%
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    감소시키며
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    업무
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    성과를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    90%
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    향상시키는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이유를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    알아보았죠.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이제
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    기업에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    왜
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    효과적인지
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이해하셨을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    겁니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  With that comes the crucial question:{' '}
                  <span className={classes.spanItalic}>
                    How do you actually implement this in your workplace?
                  </span>
                </>,
                <>
                  Esto nos lleva a la pregunta crucial:
                  <span className={classes.spanItalic}>
                    ¿Cómo se implementa esto realmente en el lugar de trabajo?
                  </span>
                </>,
                <>
                  Cela soulève la question cruciale&nbsp;:{' '}
                  <span className={classes.spanItalic}>
                    Comment mettre cela en œuvre concrètement sur votre lieu de
                    travail&nbsp;?
                  </span>
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그렇다면
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    여기서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    중요한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    질문이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    나옵니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실제로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    직장에서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이것을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    어떻게
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    구현할
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있을까요?
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Whether you’re managing a traditional office, operating a
                  coworking space, or leading a distributed remote team, this
                  guide provides actionable strategies to transform sound design
                  from theory into measurable results, and if you have questions
                  specific to your business, contact us&nbsp;—{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST + '/contact'}
                    title={
                      'Contact us to begin streaming fully licensed, professional background music in your business'
                    }
                    rel="noopener noreferrer"
                  >
                    <span translate="no">
                      Practical&nbsp;
                      <span className={classes.spanPre}>
                        Stream&nbsp;
                        <IconExternalLink />
                      </span>
                    </span>
                  </a>
                </>,
                <>
                  Ya sea que gestione una oficina tradicional, un espacio de
                  coworking o un equipo remoto distribuido, esta guía le
                  proporciona estrategias prácticas para transformar el diseño
                  de sonido de la teoría a resultados medibles. Si tiene
                  preguntas específicas sobre su negocio, contáctenos:{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST + '/contact'}
                    title={
                      'Contáctenos para empezar a reproducir música ambiental profesional y con licencia en su establecimiento'
                    }
                    rel="noopener noreferrer"
                  >
                    <span translate="no">
                      Practical&nbsp;
                      <span className={classes.spanPre}>
                        Stream&nbsp;
                        <IconExternalLink />
                      </span>
                    </span>
                  </a>
                </>,
                <>
                  Que vous gériez un bureau traditionnel, un espace de coworking
                  ou une équipe distante et répartie, ce guide vous propose des
                  stratégies concrètes pour transformer la conception sonore, de
                  la théorie à des résultats mesurables. Si vous avez des
                  questions spécifiques à votre entreprise, n'hésitez pas à nous
                  contacter&nbsp;:{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST + '/contact'}
                    title={
                      "Contactez-nous pour commencer à diffuser de la musique d'ambiance professionnelle et sous licence dans votre établissement"
                    }
                    rel="noopener noreferrer"
                  >
                    <span translate="no">
                      Practical&nbsp;
                      <span className={classes.spanPre}>
                        Stream&nbsp;
                        <IconExternalLink />
                      </span>
                    </span>
                  </a>
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    전통적인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사무실,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    공유
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    오피스
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    또는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분산된
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    원격
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    근무
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    팀
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    등
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    어떤
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    환경에서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    근무하시든,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    가이드는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음향
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    설계
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이론을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실질적인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    결과로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    구현하는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    데
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    도움이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    되는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실용적인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    전략을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    제공합니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    회사별
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    맞춤형
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    문의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사항은
                  </span>{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST + '/contact'}
                    title={
                      '귀사의 사업장에서 정식 라이선스를 받은 전문 배경 음악 스트리밍 서비스를 시작하려면 저희에게 문의하세요.'
                    }
                    rel="noopener noreferrer"
                  >
                    <span translate="no">
                      Practical&nbsp;
                      <span className={classes.spanPre}>
                        Stream&nbsp;
                        <IconExternalLink />
                      </span>
                    </span>
                  </a>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    연락주시기
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    바랍니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Now, let’s start building your strategic music implementation
                  plan.
                </>,
                <>
                  Ahora, comencemos a elaborar su plan estratégico de
                  implementación musical.
                </>,
                <>
                  Maintenant, commençons à élaborer votre plan stratégique de
                  mise en œuvre musicale.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이제
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    전략적인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    활용
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    계획을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수립해
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    보겠습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <section id={'what-to-learn-in-this-guide'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>What You’ll Learn in This Guide</>,
                  <>Lo que aprenderás en esta guía</>,
                  <>Ce que vous apprendrez dans ce guide</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가이드에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      배우게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      될
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      내용
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>Office Music Strategies</>,
                    <>Estrategias de música para la oficina</>,
                    <>Stratégies musicales pour le bureau</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사무실
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전략
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Traditional Offices: Zone-Based Design</>,
                    <>Oficinas tradicionales: Diseño basado en zonas</>,
                    <>
                      Bureaux traditionnels&nbsp;: Stratégies de conception
                      basées sur les zones pour les espaces de bureau
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전통적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사무실:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구역
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기반
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        디자인
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Coworking Spaces</>,
                    <>Espacios de coworking</>,
                    <>Espaces de coworking</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        코워킹
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스페이스
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Remote Work Sound Strategy</>,
                    <>Estrategia sólida para el trabajo remoto</>,
                    <>Stratégie efficace pour le télétravail</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        원격
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        근무
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사운드
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전략
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>4-Step Implementation Plan</>,
                    <>Plan de implementación en 4 pasos</>,
                    <>Plan de mise en œuvre en 4 étapes</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4단계
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실행
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        계획
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Step 1: Assess Work & Preferences</>,
                    <>Paso 1: Evaluar el trabajo y las preferencias</>,
                    <>Étape&nbsp;1 : Évaluer le travail et les préférences</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        1단계:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        업무
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        및
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선호도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        평가
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Step 2: Create Audio Zones</>,
                    <>Paso 2: Crear zonas de audio</>,
                    <>Étape 2&nbsp;: Créer des zones audio</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        2단계:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오디오
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        영역
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        생성
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Step 3: Balance Control & Community</>,
                    <>Paso 3: Equilibrio entre control y comunidad</>,
                    <>
                      Étape 3&nbsp;: Équilibrer le contrôle et la participation
                      communautaire
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3단계:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        균형
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        잡힌
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        통제와
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        커뮤니티
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구축
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Step 4: Measure & Optimize</>,
                    <>Paso 4: Medir y optimizar</>,
                    <>Étape 4&nbsp;: Mesurer et optimiser</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4단계:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        측정
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        및
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        최적화
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Measuring Success & ROI</>,
                    <>Medición del éxito y del retorno de la inversión (ROI)</>,
                    <>Mesurer le succès et le retour sur investissement</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        성공
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        및
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        투자
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수익률
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        측정
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>7 Common Mistakes</>,
                    <>7 errores comunes</>,
                    <>7 erreurs courantes</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흔히
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저지르는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        7가지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실수
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Week-by-Week Checklist</>,
                    <>Lista de verificación semana a semana</>,
                    <>Liste de contrôle semaine par semaine</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주별
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        체크리스트
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Competitive Advantages</>,
                    <>Ventajas competitivas</>,
                    <>Avantages concurrentiels</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경쟁
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        우위
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Next Steps & Resources</>,
                    <>Próximos pasos y recursos</>,
                    <>Prochaines étapes et ressources</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        단계
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        및
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        자료
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
            </section>
            <section id={'music-strategies-vary-by-environment'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>Music Strategies for Every Work Environment</>,
                  <>Estrategias musicales para cada entorno de trabajo</>,
                  <>
                    Stratégies musicales adaptées à chaque environnement de
                    travail
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      업무
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      환경에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      맞는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전략
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Different work environments face unique acoustic challenges
                    and opportunities. Let’s explore proven strategies for each
                    setting.
                  </>,
                  <>
                    Los diferentes entornos laborales presentan desafíos y
                    oportunidades acústicas únicos. Exploremos estrategias
                    probadas para cada entorno.
                  </>,
                  <>
                    Les différents environnements de travail présentent des
                    défis et des opportunités acoustiques spécifiques. Explorons
                    ensemble des stratégies éprouvées pour chaque contexte.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다양한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      업무
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      환경은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고유한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음향적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      과제와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기회를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가지고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      각
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      환경에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      맞는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      검증된
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전략들을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      살펴보겠습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id={'traditional-offices-zone-based-audio'}>
                <h3 className={classes.subSection}>
                  {translate(
                    <>
                      Traditional Office Spaces: The Zone-Based Audio Design
                      System
                    </>,
                    <>
                      Espacios de oficina tradicionales: El sistema de diseño de
                      audio basado en zonas
                    </>,
                    <>
                      Espaces de bureaux traditionnels&nbsp;: Le système de
                      conception audio basé sur des zones
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전통적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사무
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        공간:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구역
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기반
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오디오
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        디자인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시스템
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The biggest mistake companies make? Treating the entire
                      office as a single acoustic space with one audio solution.
                      Different work activities require fundamentally different
                      sound environments.
                    </>,
                    <>
                      ¿El mayor error que cometen las empresas? Tratar toda la
                      oficina como un único espacio acústico con una sola
                      solución de audio. Las diferentes actividades laborales
                      requieren entornos sonoros fundamentalmente diferentes.
                    </>,
                    <>
                      La plus grande erreur que commettent les
                      entreprises&nbsp;? Traiter l'ensemble des bureaux comme un
                      seul espace acoustique avec une solution audio unique. Or,
                      les différentes activités professionnelles nécessitent des
                      environnements sonores fondamentalement différents.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기업들이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저지르는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        큰
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실수는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        무엇일까요?
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사무실
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전체를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        하나의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        공간으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        간주하고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        하나의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오디오
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        솔루션만
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것입니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다양한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        업무
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        활동에는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        근본적으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다른
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        환경이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        필요합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <span className={classes.pParagraph}>
                  {translate(
                    <>
                      The most successful sound design for offices creates
                      distinct audio zones aligned with work modes and cognitive
                      demands.
                    </>,
                    <>
                      El diseño de sonido más eficaz para oficinas crea zonas de
                      audio diferenciadas que se adaptan a los modos de trabajo
                      y a las exigencias cognitivas.
                    </>,
                    <>
                      La conception sonore la plus réussie pour les bureaux crée
                      des zones audio distinctes, adaptées aux modes de travail
                      et aux exigences cognitives.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사무실
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        환경에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        성공적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        디자인은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        업무
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        인지적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        요구
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사항에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        맞춰
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        명확하게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구분된
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구역을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        조성하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </span>
                <section id={'music-focus-zones-for-concentration'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Focus Zones: The Deep Work Sanctuary (40–50 decibels)
                      </>,
                      <>
                        Zonas de concentración: El santuario del trabajo
                        profundo (40-50 decibelios)
                      </>,
                      <>
                        Zones de concentration&nbsp;: Le sanctuaire du travail
                        intensif (40 à 50 décibels)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          집중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          몰입
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (40~50
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          데시벨)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        These areas serve employees tackling complex analytical
                        tasks, intensive writing, detailed coding, financial
                        modeling, or any work requiring sustained concentration
                        without interruption.
                      </>,
                      <>
                        Estas áreas están destinadas a empleados que realizan
                        tareas analíticas complejas, redacción intensiva,
                        programación detallada, modelado financiero o cualquier
                        trabajo que requiera concentración sostenida sin
                        interrupciones.
                      </>,
                      <>
                        Ces espaces sont destinés aux employés qui effectuent
                        des tâches analytiques complexes, de la rédaction
                        intensive, du codage détaillé, de la modélisation
                        financière ou tout autre travail nécessitant une
                        concentration soutenue et sans interruption.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          복잡한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분석
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          글쓰기,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          상세한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          코딩,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          재무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모델링
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          방해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          없이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지속적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중력이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수행하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          마련되었습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'music-focus-zones-audio-qualities'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Audio characteristics:</>,
                        <>Características de audio:</>,
                        <>Caractéristiques audio&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오디오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특성:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Volume at library-quiet levels (40–50&nbsp;dB)</>,
                          <>
                            Volumen a niveles propios de una biblioteca
                            (40-50&nbsp;dB)
                          </>,
                          <>
                            Volume sonore au niveau d'une bibliothèque (40 à
                            50&nbsp;dB)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              도서관처럼
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조용한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수준(40~50데시벨)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Ambient, classical, or lo-fi instrumental
                            exclusively
                          </>,
                          <>
                            Música ambiental, clásica o instrumental lo-fi
                            exclusivamente.
                          </>,
                          <>
                            Musique d'ambiance, classique ou instrumentale lo-fi
                            exclusivement.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              앰비언트,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              클래식
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              로파이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              연주곡만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제공합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Absolutely no vocals or lyrics</>,
                          <>Sin voces ni letras de ningún tipo</>,
                          <>Absolument aucune voix ni paroles.</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              보컬이나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가사는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전혀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              없습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Consistent, non-disruptive soundscapes</>,
                          <>Paisajes sonoros consistentes y no perturbadores</>,
                          <>
                            Des paysages sonores cohérents et non perturbateurs
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              일관되고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방해되지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음향
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              환경
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Predictable patterns without sudden dynamic changes
                          </>,
                          <>
                            Patrones predecibles sin cambios dinámicos
                            repentinos
                          </>,
                          <>
                            Des schémas prévisibles sans changements dynamiques
                            soudains
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              갑작스러운
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              역동적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              변화
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              없이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              예측
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              패턴
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <p
                    className={classes.pParagraph}
                    style={{ marginTop: '45px' }}
                  >
                    {translate(
                      <>
                        Install sound masking systems if budget allows. These
                        generate subtle white or pink noise that masks
                        intermittent sounds (keyboard clicks, distant
                        conversations) far more effectively than complete
                        silence.
                      </>,
                      <>
                        Si el presupuesto lo permite, instale sistemas de
                        enmascaramiento de sonido. Estos generan un ruido blanco
                        o rosa sutil que enmascara los sonidos intermitentes
                        (clics del teclado, conversaciones lejanas) de forma
                        mucho más eficaz que el silencio absoluto.
                      </>,
                      <>
                        Installez des systèmes de masquage sonore si votre
                        budget le permet. Ces systèmes génèrent un bruit blanc
                        ou rose subtil qui masque les bruits intermittents
                        (clics de clavier, conversations lointaines) bien plus
                        efficacement qu'un silence complet.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예산이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          허락한다면
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차단
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시스템을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          설치하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시스템은 미묘한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          백색
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음이나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분홍색
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          발생시켜
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          간헐적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음(키보드
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소리,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          멀리서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          들려오는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대화
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소리
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          등)을 완전한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          침묵보다
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          훨씬
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차단합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Use physical markers&nbsp;— signage, floor patterns, or
                        lighting changes&nbsp;— to clearly delineate focus
                        zones. Employees should know immediately when they’re
                        entering a deep work area.
                      </>,
                      <>
                        Utilice marcadores físicos (señalización, patrones en el
                        suelo o cambios en la iluminación) para delimitar
                        claramente las zonas de concentración. Los empleados
                        deben saber de inmediato cuándo están entrando en un
                        área de trabajo que requiere concentración. Sistemas de
                        enmascaramiento de sonido: Estos generan un ruido blanco
                        o rosa sutil que enmascara los sonidos intermitentes
                        (clics del teclado, conversaciones lejanas) de forma
                        mucho más eficaz que el silencio absoluto.
                      </>,
                      <>
                        Utilisez des repères physiques (signalisation, motifs au
                        sol ou changements d'éclairage) pour délimiter
                        clairement les zones de concentration. Les employés
                        doivent savoir immédiatement lorsqu'ils entrent dans une
                        zone de travail nécessitant une concentration intense.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          표지판,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          바닥
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          패턴
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조명
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          변화와
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          같은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          물리적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          표시물을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하여
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          명확하게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구분하십시오. 직원들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          들어설
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          때
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          즉시
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인지할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있어야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Establish social norms through team agreements: no
                        spontaneous conversations in focus zones, headphone use
                        is encouraged, and “focus time” blocks on shared
                        calendars are respected.
                      </>,
                      <>
                        Establecer normas sociales mediante acuerdos de equipo:
                        no se permiten conversaciones espontáneas en las zonas
                        de concentración, se fomenta el uso de auriculares y se
                        respetan los bloques de "tiempo de concentración" en los
                        calendarios compartidos.
                      </>,
                      <>
                        Établissez des normes sociales par le biais d'accords
                        d'équipe&nbsp;: pas de conversations spontanées dans les
                        zones de concentration, l'utilisation d'écouteurs est
                        encouragée et les plages horaires dédiées au travail
                        concentré sur les calendriers partagés sont respectées.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          팀
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합의를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사회적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          규범을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          확립하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역에서는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          즉흥적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대화를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          자제하고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          헤드폰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          권장하며,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공유
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          캘린더에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          표시된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          "집중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간"
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          블록을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          존중합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'examples-of-music-for-focus-zones'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Music selection examples:</>,
                        <>Ejemplos de selección musical:</>,
                        <>Exemples de sélection musicale&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          ></span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Brian Eno’s ambient works</>,
                          <>Las obras de música ambiental de Brian Eno</>,
                          <>Les œuvres de musique ambiante de Brian Eno</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선택
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              예시:
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Max Richter’s minimalist compositions</>,
                          <>Las composiciones minimalistas de Max Richter</>,
                          <>Les compositions minimalistes de Max Richter</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              맥스
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              리히터의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              미니멀리즘
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작곡
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Ólafur Arnalds’ atmospheric piano</>,
                          <>
                            Composiciones minimalistas para pianos atmosféricos
                            de Ólafur Arnalds
                          </>,
                          <>Le piano atmosphérique d'Ólafur Arnalds</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              올라푸르
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아르날즈(Ólafur
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Arnalds)의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분위기
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피아노
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Curated lo-fi study playlists</>,
                          <>
                            Listas de reproducción de música lo-fi seleccionadas
                            para estudiar
                          </>,
                          <>Playlists d'étude lo-fi sélectionnées avec soin</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              엄선된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              로파이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공부
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Classical baroque at low volume</>,
                          <>Música clásica barroca a bajo volumen</>,
                          <>Musique baroque classique à faible volume</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              낮은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              감상하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              고전
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              바로크
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'music-for-collaborative-areas'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Collaborative Areas: The Creative Energy Space (60–70
                        decibels)
                      </>,
                      <>
                        Áreas de colaboración: El espacio de energía creativa
                        (60-70 decibelios)
                      </>,
                      <>
                        Espaces de collaboration&nbsp;: L'espace d'énergie
                        créative (60 à 70 ​​ décibels)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          협업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          창의적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          에너지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (60~70데시벨)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        These zones support brainstorming sessions, team
                        meetings, design reviews, creative problem-solving, and
                        any work benefiting from energized interaction.
                      </>,
                      <>
                        Estas zonas facilitan sesiones de lluvia de ideas,
                        reuniones de equipo, revisiones de diseño, resolución
                        creativa de problemas y cualquier trabajo que se
                        beneficie de una interacción dinámica.
                      </>,
                      <>
                        Ces espaces sont propices aux séances de brainstorming,
                        aux réunions d'équipe, aux revues de conception, à la
                        résolution créative de problèmes et à tout travail qui
                        bénéficie d'une interaction dynamique.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          브레인스토밍
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          세션,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          팀
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          회의,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          디자인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          검토,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          창의적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          문제
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          해결
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활발한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          상호
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작용을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          얻을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          종류의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지원합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'collaborative-areas-audio-qualities'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Audio characteristics:</>,
                        <>Características de audio:</>,
                        <>Caractéristiques audio&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오디오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특성:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Moderate volume (60–70&nbsp;dB) that allows easy
                            conversation without shouting
                          </>,
                          <>
                            Volumen moderado (60-70&nbsp;dB) que permite una
                            conversación fluida sin necesidad de gritar.
                          </>,
                          <>
                            Volume sonore modéré (60 à 70 dB) permettant une
                            conversation aisée sans avoir besoin de crier.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              적당한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음량(60~70데시벨)에서는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소리를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지르지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않고도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              편안하게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대화를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              나눌
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Upbeat indie, jazz, or instrumental pop</>,
                          <>Música indie animada, jazz o pop instrumental</>,
                          <>
                            Musique indie entraînante, jazz ou pop instrumentale
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              경쾌한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              인디
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재즈
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팝
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Faster tempos (100–130&nbsp;BPM) that energize
                            without overwhelming
                          </>,
                          <>
                            Ritmos más rápidos (100-130&nbsp;BPM) que energizan
                            sin resultar abrumadores.
                          </>,
                          <>
                            Des tempos plus rapides (100 à 130 BPM) qui
                            dynamisent sans être submergents.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              과도하게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부담스럽지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않으면서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              활력을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              불어넣는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              빠른
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              템포(100~130BPM)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Music is interesting enough to create an atmosphere
                            without demanding attention
                          </>,
                          <>
                            La música es lo suficientemente interesante como
                            para crear una atmósfera sin exigir atención.
                          </>,
                          <>
                            La musique est suffisamment intéressante pour créer
                            une atmosphère sans pour autant exiger l'attention.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              음악은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주의를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              요구하지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않으면서도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분위기를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조성할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만큼
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              충분히
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              흥미롭습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'collaborative-areas-design-thoughts'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Design considerations:</>,
                        <>Consideraciones de diseño:</>,
                        <>Considérations relatives à la conception&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            디자인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고려
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사항:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Acoustic treatment matters. Install sound-absorbing
                          panels to prevent noise from collaborative areas
                          bleeding into focus zones.
                        </>,
                        <>
                          El tratamiento acústico es importante. Instale paneles
                          fonoabsorbentes para evitar que el ruido de las zonas
                          de trabajo colaborativo se propague a las zonas de
                          concentración.
                        </>,
                        <>
                          Le traitement acoustique est important. Installez des
                          panneaux insonorisants pour empêcher le bruit des
                          espaces de travail collaboratifs de se propager dans
                          les zones de concentration.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음향
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            처리는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중요합니다.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            협업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소음이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            작업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            새어
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            들어가지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            않도록
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            흡음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            패널을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설치하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Position collaborative spaces strategically&nbsp;—
                          ideally separated from focus areas by physical
                          barriers (walls, tall dividers) or significant
                          distance.
                        </>,
                        <>
                          Ubique los espacios de colaboración estratégicamente,
                          idealmente separados de las áreas de trabajo
                          individual mediante barreras físicas (paredes,
                          separadores altos) o una distancia considerable.
                        </>,
                        <>
                          Positionnez les espaces de collaboration de manière
                          stratégique, idéalement séparés des zones de travail
                          individuel par des barrières physiques (murs, cloisons
                          hautes) ou une distance suffisante.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            협업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전략적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            배치해야
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하며,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이상적으로는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            물리적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장벽(벽,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            높은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            칸막이)이나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            상당한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            거리를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            통해
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분리되어
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있어야
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Create visual and auditory transitions between zones
                          so employees mentally shift modes as they move between
                          spaces.
                        </>,
                        <>
                          Cree transiciones visuales y auditivas entre las zonas
                          para que los empleados cambien de mentalidad a medida
                          que se desplazan entre los diferentes espacios.
                        </>,
                        <>
                          Créez des transitions visuelles et auditives entre les
                          zones afin que les employés changent d'état d'esprit
                          lorsqu'ils se déplacent d'un espace à l'autre.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            직원들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이동할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정신적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모드를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전환할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있도록
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구역
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            간에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시각적
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            및
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            청각적
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전환
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            효과를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            만드세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'collaborative-areas-music-examples'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Music selection examples:</>,
                        <>Ejemplos de selección musical:</>,
                        <>Exemples de sélection musicale&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            음악
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            선택
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            예시:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Snarky Puppy’s upbeat jazz fusion</>,
                          <>La enérgica fusión de jazz de Snarky Puppy</>,
                          <>Le jazz fusion entraînant de Snarky Puppy</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              Snarky
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Puppy의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경쾌한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재즈
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              퓨전
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Khruangbin’s instrumental psychedelic rock</>,
                          <>El rock psicodélico instrumental de Khruangbin</>,
                          <>Le rock psychédélique instrumental de Khruangbin</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              크루앙빈의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              연주
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              중심의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사이키델릭
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              록
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Bonobo’s downtempo electronic</>,
                          <>Música electrónica downtempo de Bonobo</>,
                          <>Musique électronique downtempo de Bonobo</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              보노보의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다운템포
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일렉트로닉
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Thievery Corporation’s lounge music</>,
                          <>Música lounge de Thievery Corporation</>,
                          <>La musique lounge de Thievery Corporation</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              <span
                                aria-hidden={true}
                                className={classes.asianTextBlock}
                              >
                                Thievery
                              </span>{' '}
                              <span
                                className={classes.asianTextBlock}
                                aria-hidden={true}
                              >
                                Corporation의
                              </span>{' '}
                              <span
                                className={classes.asianTextBlock}
                                aria-hidden={true}
                              >
                                라운지
                              </span>{' '}
                              <span
                                className={classes.asianTextBlock}
                                aria-hidden={true}
                              >
                                음악
                              </span>
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Contemporary jazz compilations</>,
                          <>Recopilaciones de jazz contemporáneo</>,
                          <>Compilations de jazz contemporain</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              현대
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재즈
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              컴필레이션
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'break-room-music-design'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Break Rooms: The Employee Choice Zone (Flexible volume)
                      </>,
                      <>
                        Salas de descanso: La zona de elección de los empleados
                        (Volumen flexible)
                      </>,
                      <>
                        Espaces de détente&nbsp;: La zone de choix des employés
                        (Volume flexible)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          휴게실:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선택하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (유연한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활용)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        These spaces aren’t about productivity&nbsp;— they’re
                        about recharging, social connection, and giving
                        employees autonomy over their environment.
                      </>,
                      <>
                        Estos espacios no tienen que ver con la productividad,
                        sino con recargar energías, la conexión social y brindar
                        a los empleados autonomía sobre su entorno.
                      </>,
                      <>
                        Ces espaces ne sont pas axés sur la productivité, mais
                        sur la régénération, les interactions sociales et
                        l'autonomie des employés quant à leur environnement de
                        travail.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생산성
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          향상을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아니라,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          재충전,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사회적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          교류,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그리고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들에게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          자신의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          자율성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부여하기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'break-room-music-audio-qualities'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Audio characteristics:</>,
                        <>Características de audio:</>,
                        <>Caractéristiques audio&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오디오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특성:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Employee-controlled playlists</>,
                          <>
                            Listas de reproducción controladas por los empleados
                          </>,
                          <>Listes de lecture contrôlées par les employés</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              직원이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관리하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재생
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목록
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Higher volume acceptable (up&nbsp;to 75&nbsp;dB)</>,
                          <>Volumen más alto aceptable (hasta 75&nbsp;dB)</>,
                          <>
                            Un volume plus élevé est acceptable (jusqu'à 75 dB).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              더
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              높은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음량도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              허용됩니다
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (최대
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              75데시벨).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Lyrics and personal music preferences encouraged</>,
                          <>
                            Se anima a compartir letras de canciones y
                            preferencias musicales personales.
                          </>,
                          <>
                            Les paroles de chansons et les préférences musicales
                            personnelles sont les bienvenues.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              가사와
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개인적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              취향을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              자유롭게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표현해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Genre variety reflecting team diversity</>,
                          <>
                            Variedad de géneros que refleja la diversidad del
                            equipo.
                          </>,
                          <>
                            Une variété de genres reflétant la diversité de
                            l'équipe.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              팀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구성원의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양성을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              반영하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장르
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'break-room-music-design-thoughts'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Design considerations:</>,
                        <>Consideraciones de diseño:</>,
                        <>Considérations relatives à la conception&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            디자인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고려
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사항:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Implement a fair rotation system for playlist control.
                          Consider daily or weekly playlist managers to ensure
                          everyone gets representation.
                        </>,
                        <>
                          Implementa un sistema de rotación equitativo para el
                          control de las listas de reproducción. Considera la
                          posibilidad de asignar responsables de las listas de
                          reproducción de forma diaria o semanal para garantizar
                          que todos tengan representación.
                        </>,
                        <>
                          Mettez en place un système de rotation équitable pour
                          la gestion des listes de lecture. Envisagez de
                          désigner des responsables des listes de lecture
                          quotidiens ou hebdomadaires afin de garantir que
                          chacun soit représenté.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            플레이리스트
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관리를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            위한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공정한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            순환
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시스템을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구현하세요. 모든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사람이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            참여할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있도록
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            매일
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            또는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            매주
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관리자를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지정하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            것을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고려해
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Set only minimal guidelines: no explicit content, no
                          politically charged material, no extremely jarring
                          genres unless there’s team consensus.
                        </>,
                        <>
                          Establezcan solo pautas mínimas: nada de contenido
                          explícito, nada de material con connotaciones
                          políticas, ni géneros extremadamente discordantes a
                          menos que haya consenso en el equipo.
                        </>,
                        <>
                          Établissez des règles minimales&nbsp;: pas de contenu
                          explicite, pas de contenu à caractère politique, pas
                          de genres extrêmement choquants sauf si l'équipe est
                          unanime.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            최소한의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지침만
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설정하세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노골적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            콘텐츠,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정치적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편향된
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자료,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            그리고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            팀
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구성원들의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            합의가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            극도로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            불쾌감을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            주는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장르는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            허용하지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            않습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Encourage employees to contribute their favorite
                          songs, building shared playlists that reflect your
                          culture and create connection points for team members
                          to discover common interests.
                        </>,
                        <>
                          Anime a los empleados a compartir sus canciones
                          favoritas, creando listas de reproducción compartidas
                          que reflejen la cultura de la empresa y sirvan como
                          puntos de conexión para que los miembros del equipo
                          descubran intereses comunes.
                        </>,
                        <>
                          Encouragez vos employés à partager leurs chansons
                          préférées, en créant des listes de lecture partagées
                          qui reflètent votre culture d'entreprise et permettent
                          aux membres de l'équipe de découvrir des intérêts
                          communs.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            직원들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋아하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노래를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공유하도록
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장려하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            회사의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            문화를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            반영하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공유
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            만들고,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            팀원들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공통
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관심사를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            발견할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소통의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            마련하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                </section>
                <section id={'reception-area-background-music'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Reception and Common Areas: The Brand Experience (55–65
                        decibels)
                      </>,
                      <>
                        Recepción y zonas comunes: La experiencia de la marca
                        (55-65 decibelios)
                      </>,
                      <>
                        Réception et espaces communs&nbsp;: L'expérience de la
                        marque (55 à 65 décibels)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          접수처
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          브랜드
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경험
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (55~65데시벨)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        These spaces communicate the company culture to visitors
                        while creating a professional atmosphere for employees
                        passing through.
                      </>,
                      <>
                        Estos espacios transmiten la cultura de la empresa a los
                        visitantes, al mismo tiempo que crean un ambiente
                        profesional para los empleados que transitan por ellos.
                      </>,
                      <>
                        Ces espaces transmettent la culture d'entreprise aux
                        visiteurs tout en créant une atmosphère professionnelle
                        pour les employés qui y circulent.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          방문객들에게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          회사
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          문화를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전달하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          동시에,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        ></span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이곳을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지나가는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들에게는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전문적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분위기를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조성합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'reception-areas-audio-qualities'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Audio characteristics:</>,
                        <>Características de audio:</>,
                        <>Caractéristiques audio&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오디오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특성:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Professional but not generic</>,
                          <>Profesional pero no genérica</>,
                          <>Professionnel, mais pas générique</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              전문적이지만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              틀에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              박히지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않은
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Volume that allows comfortable conversation
                            (55–65&nbsp;dB)
                          </>,
                          <>
                            Volumen que permite una conversación cómoda
                            (55-65&nbsp;dB)
                          </>,
                          <>
                            Un volume sonore permettant une conversation
                            confortable (55 à 65 dB)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              편안하게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대화할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음량
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (55~65데시벨)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Reflects brand personality (modern tech startup vs.
                            traditional law firm)
                          </>,
                          <>
                            Refleja la personalidad de la marca (empresa
                            emergente de tecnología moderna frente a bufete de
                            abogados tradicional)
                          </>,
                          <>
                            Reflète la personnalité de la marque (start-up
                            technologique moderne vs. cabinet d'avocats
                            traditionnel)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              브랜드
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개성을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              반영합니다
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (현대적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기술
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              스타트업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전통적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              법률
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회사)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Consistently pleasant without being memorable</>,
                          <>
                            Agradable en todo momento, pero sin ser memorable
                          </>,
                          <>
                            Agréable de bout en bout, sans pour autant être
                            mémorable
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              꾸준히
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              즐거운
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              곳이지만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              특별히
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기억에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              남을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              곳은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아닙니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'reception-area-music-design-thoughts'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Design considerations:</>,
                        <>Consideraciones de diseño:</>,
                        <>Considérations relatives à la conception&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            디자인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고려
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사항:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Hire a professional service or consultant to curate
                          reception playlists aligned with brand identity. This
                          isn’t just background noise&nbsp;— it’s an integral
                          part of your brand experience.
                        </>,
                        <>
                          Contrate un servicio o consultor profesional para
                          crear listas de reproducción para la recepción que
                          estén en sintonía con la identidad de su marca. No se
                          trata solo de música de fondo, sino de una parte
                          integral de la experiencia de su marca.
                        </>,
                        <>
                          Faites appel à un service ou un consultant
                          professionnel pour créer des listes de lecture
                          adaptées à l'identité de votre marque. Il ne s'agit
                          pas d'un simple bruit de fond, mais d'une partie
                          intégrante de l'expérience de votre marque.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            전문
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업체나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            컨설턴트를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고용하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            브랜드
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            아이덴티티에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            맞는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            리셉션
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구성하세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            단순히
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            배경
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소음이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            아니라
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            브랜드
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경험의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            필수적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            요소입니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Avoid overplayed commercial radio hits. They feel
                          cheap and diminish brand perception.
                        </>,
                        <>
                          Evita los éxitos comerciales de la radio que se
                          reproducen constantemente. Suenan baratos y perjudican
                          la imagen de la marca.
                        </>,
                        <>
                          Évitez les tubes commerciaux trop diffusés à la radio.
                          Ils donnent une impression de bas de gamme et nuisent
                          à l'image de marque.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            너무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            흔하게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            나오는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            상업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            라디오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            히트곡은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피하세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            그런
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            곡들은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            저렴하게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            느껴지고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            브랜드
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이미지를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            손상시킬
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Update playlists seasonally to maintain freshness
                          without constant changes that create inconsistency.
                        </>,
                        <>
                          Actualiza las listas de reproducción según la
                          temporada para mantenerlas frescas sin necesidad de
                          cambios constantes que generen inconsistencia.
                        </>,
                        <>
                          Mettez à jour vos playlists au fil des saisons pour
                          maintenir leur fraîcheur sans changements constants
                          qui créent de l'incohérence.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            계절에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            맞춰
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업데이트하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            일관성을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            해치지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            않으면서도
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            신선함을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>{' '}
                </section>
              </section>
              <hr className={classes.threeDotDivisor} />
              <section id={'coworking-spaces-music'}>
                <h3 className={classes.subSection}>
                  {translate(
                    <>
                      Music in Coworking Spaces: Navigating Diverse Needs and
                      Preferences
                    </>,
                    <>
                      Música en espacios de coworking: cómo gestionar las
                      diversas necesidades y preferencias
                    </>,
                    <>
                      La musique dans les espaces de coworking&nbsp;: concilier
                      les besoins et les préférences variés.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        코워킹
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스페이스에서의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다양한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        요구와
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선호도를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고려한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        접근
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Coworking spaces face unique challenges. Research shows
                      music divides coworking members almost evenly&nbsp;— 53%
                      love background music, while 45% find it distracting. This
                      split creates tension that requires sophisticated
                      solutions.
                    </>,
                    <>
                      Los espacios de coworking se enfrentan a desafíos únicos.
                      Las investigaciones demuestran que la música divide a los
                      miembros de estos espacios casi por igual: el 53% disfruta
                      de la música de fondo, mientras que al 45% le resulta una
                      distracción. Esta división genera tensión que requiere
                      soluciones sofisticadas.
                    </>,
                    <>
                      Les espaces de coworking sont confrontés à des défis
                      particuliers. Des études montrent que la musique divise
                      les membres de ces espaces de manière quasi égale&nbsp;:
                      53&nbsp;% apprécient la musique d'ambiance, tandis que 45
                      % la trouvent distrayante. Cette divergence crée des
                      tensions qui nécessitent des solutions adaptées.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        코워킹
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스페이스는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고유한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        어려움에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        직면해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        연구에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        따르면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        코워킹
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        공간
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이용자들의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        의견을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        거의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        절반으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        나누는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        나타났습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        53%는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        배경
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        좋아하지만,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        45%는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방해가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        된다고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        생각합니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이러한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        의견
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        차이는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        정교한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        해결책이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        필요한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        갈등을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        야기합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Sound design expert Brian d’Souza’s research identified
                      critical success factors for music in coworking spaces.
                    </>,
                    <>
                      La investigación del experto en diseño de sonido Brian
                      d’Souza identificó los factores clave para el éxito de la
                      música en los espacios de coworking.
                    </>,
                    <>
                      Les recherches de Brian d’Souza, expert en conception
                      sonore, ont permis d’identifier les facteurs clés de
                      succès pour la musique dans les espaces de coworking.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        디자인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전문가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        브라이언
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        드수자의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        연구는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        코워킹
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스페이스에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        성공에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있어
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        중요한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        요소들을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        밝혀냈습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'optimal-audio-in-coworking-spaces'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>The Optimal Volume Formula: 65&nbsp;Decibels</>,
                      <>La fórmula del volumen óptimo: 65 decibelios</>,
                      <>La formule du volume optimal&nbsp;: 65 décibels</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          볼륨
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공식:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          65데시벨
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Through extensive testing, d’Souza determined that 65
                        decibels represents the sweet spot for coworking
                        environments:
                      </>,
                      <>
                        Tras extensas pruebas, D'Souza determinó que 65
                        decibelios representan el nivel óptimo para los entornos
                        de trabajo compartido:
                      </>,
                      <>
                        Grâce à des tests approfondis, d'Souza a déterminé que
                        65 décibels représentaient le niveau sonore idéal pour
                        les espaces de coworking&nbsp;:
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          광범위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          테스트를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          드수자는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          65데시벨이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          코워킹
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          적합한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수준이라는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결론을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          내렸습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Loud enough to mask disruptive keyboard clicks, mouse
                          clicks, and occasional conversations
                        </>,
                        <>
                          Lo suficientemente alto como para enmascarar los
                          molestos clics del teclado, los clics del ratón y las
                          conversaciones ocasionales
                        </>,
                        <>
                          Suffisamment fort pour masquer les clics de clavier,
                          les clics de souris et les conversations
                          occasionnelles
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            방해되는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            키보드
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소음,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            마우스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            클릭
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소리,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            그리고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            간헐적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대화
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소리를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            충분히
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            덮을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            만큼
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            큰
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소리입니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Quiet enough not to interfere with phone calls or
                          video conferences
                        </>,
                        <>
                          Lo suficientemente silencioso como para no interferir
                          con las llamadas telefónicas o las videoconferencias
                        </>,
                        <>
                          Suffisamment silencieux pour ne pas gêner les appels
                          téléphoniques ou les visioconférences
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            전화
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            통화나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            화상
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            회의에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            방해가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            되지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            않을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정도로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조용합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Comfortable sufficient that members stop consciously
                          noticing it after 10–15 minutes
                        </>,
                        <>
                          Lo suficientemente cómodo como para que los miembros
                          dejen de notarlo conscientemente después de 10-15
                          minutos
                        </>,
                        <>
                          Suffisamment confortable pour que les participants
                          cessent d'y prêter attention au bout de 10 à 15
                          minutes
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            회원들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            10~15분
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            후에는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            의식적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            신경
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            쓰지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            않을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정도로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Use a decibel meter app to verify actual sound levels.
                        What feels like “moderate” volume often measures far
                        higher than expected.
                      </>,
                      <>
                        Utiliza una aplicación de medición de decibelios para
                        verificar los niveles de sonido reales. Lo que parece un
                        volumen "moderado" a menudo resulta ser mucho más alto
                        de lo esperado.
                      </>,
                      <>
                        Utilisez une application de mesure de décibels pour
                        vérifier les niveaux sonores réels. Ce qui semble être
                        un volume «&nbsp;modéré&nbsp;» est souvent beaucoup plus
                        élevé que prévu
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          실제
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수준을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          확인하려면
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          데시벨
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          측정기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          앱을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          우리가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          "적당하다"고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          느끼는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          볼륨도
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실제로는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          예상보다
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          훨씬
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          높은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경우가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          많습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'zone-based-for-coworking-spaces'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>The Three-Zone Solution</>,
                      <>La solución de tres zonas</>,
                      <>La solution à trois zones</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          3구역
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          솔루션
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        The most successful coworking spaces implement clear
                        acoustic separation:
                      </>,
                      <>
                        Los espacios de coworking más exitosos implementan una
                        clara separación acústica:
                      </>,
                      <>
                        Les espaces de coworking les plus performants mettent en
                        œuvre une séparation acoustique claire&nbsp;:
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          가장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          성공적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          코워킹
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스페이스는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          명확한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음향
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분리
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시스템을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구현합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'zone-1-quiet-focus'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Quiet Focus Zone (No&nbsp;Music):</>,
                        <>Zona de concentración silenciosa (sin música):</>,
                        <>
                          Zone de concentration silencieuse (pas de
                          musique)&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            조용한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구역
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (음악
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없음):
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Absolute silence or white noise only</>,
                          <>Solo silencio absoluto o ruido blanco</>,
                          <>Silence absolu ou bruit blanc uniquement</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              완전한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              침묵
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              백색
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              허용됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>No phone calls or conversations</>,
                          <>
                            No se permiten llamadas telefónicas ni
                            conversaciones
                          </>,
                          <>Aucun appel téléphonique ni aucune conversation</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              전화
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              통화나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대화는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              금지입니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Headphones use is encouraged for those wanting
                            personal music
                          </>,
                          <>
                            Se recomienda el uso de auriculares para quienes
                            deseen escuchar música de forma individual
                          </>,
                          <>
                            L'utilisation d'écouteurs est recommandée pour ceux
                            qui souhaitent écouter de la musique en privé
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              개인적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              감상하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              싶은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분들은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              헤드폰
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              권장합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Clearly marked with visible signage</>,
                          <>Claramente señalizado con carteles visibles</>,
                          <>Clairement indiqué par une signalisation visible</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              눈에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              잘
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              띄는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표지판으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              명확하게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표시되어
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Typically, 30–40% of the available workspace</>,
                          <>
                            Normalmente, entre el 30% y el 40% del espacio de
                            trabajo disponible
                          </>,
                          <>
                            En général, cela représente 30&nbsp;à 40&nbsp;% de
                            l'espace de travail disponible
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              일반적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              30~40%입니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'zone-2-collaboration'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Collaborative Zone (Moderate&nbsp;Music):</>,
                        <>Zona de colaboración (Música moderada):</>,
                        <>
                          Zone de collaboration (Musique d'ambiance
                          modérée)&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            협업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구역
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (잔잔한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악):
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Background music at 65&nbsp;dB</>,
                          <>Música de fondo a 65&nbsp;dB</>,
                          <>Musique d'ambiance à 65 dB</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              배경
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수준은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              65데시벨입니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Phone calls and conversations are acceptable</>,
                          <>
                            Las llamadas telefónicas y las conversaciones son
                            aceptables
                          </>,
                          <>
                            Les appels téléphoniques et les conversations sont
                            acceptables
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              전화
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              통화
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              및
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대화는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              허용됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Rotating genres throughout the day</>,
                          <>Alternando géneros musicales a lo largo del día</>,
                          <>
                            Alternance de genres musicaux tout au long de la
                            journée
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              하루
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              종일
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장르를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              번갈아
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가며
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재생합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Typically, 40–50% of the available workspace</>,
                          <>
                            Normalmente, entre el 40% y el 50% del espacio de
                            trabajo disponible
                          </>,
                          <>
                            En général, 40 à 50&nbsp;% de l'espace de travail
                            disponible
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              일반적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              40~50%
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'zone-3-break-time'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Social/Break Zone (Higher&nbsp;Volume):</>,
                        <>Zona social/de descanso (mayor&nbsp;volumen):</>,
                        <>
                          Espace social/détente (volume sonore plus
                          élevé)&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            소셜/휴식
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (소음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수준
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            높음):
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Music at 70–75 dB is acceptable</>,
                          <>Un nivel de música de 70-75 dB es aceptable</>,
                          <>
                            Un niveau sonore de 70 à 75 dB pour la musique est
                            acceptable
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              70~75dB
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              정도의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소리는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              괜찮습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Social interaction encouraged</>,
                          <>Se fomenta la interacción social</>,
                          <>Les interactions sociales sont encouragées</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              사회적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              교류를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              권장합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Member playlist contributions</>,
                          <>
                            Contribuciones de los miembros a la lista de
                            reproducción
                          </>,
                          <>Contributions des membres aux listes de lecture</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              회원
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기여
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Typically, 10–20% of the available workspace</>,
                          <>
                            Normalmente, entre el 10% y el 20% del espacio de
                            trabajo disponible
                          </>,
                          <>
                            En général, cela représente 10 à 20&nbsp;% de
                            l'espace de travail disponible
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              일반적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              10~20%입니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'genre-rotation-design-strategy'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>The Genre Rotation Strategy</>,
                      <>La estrategia de rotación de géneros</>,
                      <>La stratégie de rotation des genres</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          순환
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Implement a predictable daily rotation to serve
                        different working styles throughout the day:
                      </>,
                      <>
                        Implemente una rotación diaria predecible para adaptarse
                        a los diferentes estilos de trabajo a lo largo del día:
                      </>,
                      <>
                        Mettre en place une rotation quotidienne prévisible pour
                        répondre aux différents styles de travail tout au long
                        de la journée&nbsp;:
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          하루
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          종일
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다양한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스타일에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞춰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          예측
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일일
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          순환
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시스템을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구현하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'genre-rotation-in-early-mornings'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>
                          Morning (7&nbsp;am-10&nbsp;am): Gentle Instrumental
                          Focus
                        </>,
                        <>
                          Mañana (7:00 a.&nbsp;m.&nbsp;-&nbsp;10:00 a.&nbsp;m.):
                          Música instrumental suave para concentrarse
                        </>,
                        <>
                          Matin (7h-10h)&nbsp;: Musique instrumentale douce
                          propice à la concentration
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            7시~오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            10시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            잔잔한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            연주곡
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            감상
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (집중력
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            향상에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            도움)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            60–70&nbsp;BPM classical, ambient, or nature sounds
                          </>,
                          <>
                            Música clásica, ambiental o sonidos de la naturaleza
                            a 60-70 pulsaciones por minuto
                          </>,
                          <>
                            Musique classique, d'ambiance ou sons de la nature à
                            60-70 battements par minute
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              60~70BPM의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              클래식
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              앰비언트
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              자연
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소리
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Supports early risers doing deep work before crowds
                            arrive
                          </>,
                          <>
                            Ideal para quienes se levantan temprano y realizan
                            trabajo concentrado antes de que lleguen las
                            multitudes
                          </>,
                          <>
                            Il soutient les personnes qui se lèvent tôt et
                            effectuent un travail intense avant l'arrivée de la
                            foule
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              일찍
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일어나서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사람들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              몰려오기
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사람들을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지원합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Calm, non-intrusive atmosphere</>,
                          <>Ambiente tranquilo y discreto</>,
                          <>Ambiance calme et discrète</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              차분하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방해받지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분위기
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'genre-rotation-in-mid-mornings'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>
                          Mid-Morning (10&nbsp;am-1&nbsp;pm): Upbeat
                          Instrumental
                        </>,
                        <>
                          Media mañana (10:00-13:00): Música instrumental
                          animada
                        </>,
                        <>
                          En milieu de matinée (10h-13h)&nbsp;: Musique
                          instrumentale entraînante
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중반
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            10시~오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            1시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활기찬
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            연주곡
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            80–100&nbsp;BPM jazz, lo-fi, or acoustic
                            instrumental
                          </>,
                          <>
                            Música instrumental de jazz, lo-fi o acústica con un
                            ritmo de 80 a 100 pulsaciones por minuto
                          </>,
                          <>
                            Musique instrumentale jazz, lo-fi ou acoustique à
                            80-100 battements par minute
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              80~100BPM의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재즈,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              로파이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              어쿠스틱
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              연주곡
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Matches peak productivity hours for most people</>,
                          <>
                            Coincide con las horas de máxima productividad para
                            la mayoría de las personas
                          </>,
                          <>
                            Cela correspond aux heures de productivité maximale
                            pour la plupart des gens
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              Matches
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              peak
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              productivity
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              hours
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              for
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              most
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              people
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Energizing without being distracting</>,
                          <>Estimulante sin ser una distracción</>,
                          <>Énergisant sans être distrayant</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              집중을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방해하지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않으면서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              활력을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              불어넣습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'genre-rotation-in-afternoons'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Afternoon (1pm-4pm): Diverse Moderate Tempo</>,
                        <>Tarde (13:00-16:00): Ritmo moderado y variado</>,
                        <>Après-midi (13h-16h)&nbsp;: Rythme modéré et varié</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            1시~4시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            다양한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            속도
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>70–90&nbsp;BPM varied genres</>,
                          <>70-90 BPM, géneros variados</>,
                          <>70 à 90 BPM, genres variés</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              70~90
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              BPM,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장르
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Combats post-lunch energy dip</>,
                          <>
                            Combate la bajada de energía después del almuerzo
                          </>,
                          <>Combat la baisse d'énergie après le déjeuner</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              점심
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              식사
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              후
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              찾아오는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              에너지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              저하를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              해소해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              줍니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Familiar instrumental tracks</>,
                          <>Canciones instrumentales conocidas</>,
                          <>Morceaux instrumentaux familiers</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              익숙한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              연주곡들
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'rules-for-coworking-music-success'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>Critical Rules for Coworking Music Success</>,
                      <>
                        Reglas fundamentales para el éxito de la música en
                        espacios de coworking
                      </>,
                      <>
                        Règles essentielles pour réussir avec la musique dans
                        les espaces de coworking
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          코워킹
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스페이스에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          성공적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활용하기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          핵심
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          규칙
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <section id={'coworking-music-never-play-this-music'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>Never play:</>,
                        <>Nunca juegues:</>,
                        <>Ne jouez jamais&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            절대
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이하지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            마세요:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                  </section>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Familiar songs with catchy, lyrical hooks (the&nbsp;#1
                          complaint)
                        </>,
                        <>
                          Canciones conocidas con estribillos pegadizos y letras
                          fáciles de recordar (la queja número 1)
                        </>,
                        <>
                          Des chansons familières avec des refrains accrocheurs
                          et des paroles faciles à retenir (le principal
                          reproche).
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            귀에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            쏙쏙
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            들어오는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            가사와
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중독성
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            후렴구가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특징인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            익숙한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노래들
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (가장
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            흔한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            불만
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사항)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Top 40 radio hits (too recognizable and distracting)
                        </>,
                        <>
                          Éxitos de la radio Top 40 (demasiado reconocibles y
                          que distraen)
                        </>,
                        <>
                          Les tubes des 40 meilleures chansons diffusées à la
                          radio (trop reconnaissables et distrayantes)
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            인기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            라디오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            히트곡
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            40곡
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (너무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            익숙해서
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중력을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            방해할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있음)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Songs that inspire singing along or tapping</>,
                        <>
                          Canciones que invitan a cantar o a seguir el ritmo con
                          los pies
                        </>,
                        <>
                          Des chansons qui donnent envie de chanter en chœur ou
                          de taper du pied
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            함께
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            따라
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            부르거나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            박자를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            맞추고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            싶게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            만드는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노래들
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Playlists on shuffle without curation (creates jarring
                          transitions)
                        </>,
                        <>
                          Listas de reproducción en modo aleatorio sin selección
                          previa (lo que crea transiciones bruscas)
                        </>,
                        <>
                          Listes de lecture en mode aléatoire sans sélection
                          préalable (ce qui crée des transitions brutales)
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            큐레이션
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            무작위로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재생되는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (부자연스러운
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전환이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            발생합니다)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                  <section id={'coworking-music-always-provide-this'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>Always provide:</>,
                        <>Proporcione siempre:</>,
                        <>Toujours fournir&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            항상
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            다음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정보를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제공하십시오.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Visible volume controls in each zone</>,
                          <>Controles de volumen visibles en cada zona</>,
                          <>Commandes de volume visibles dans chaque zone</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              각
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조절
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장치가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표시됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Clear signage about audio policies</>,
                          <>Señalización clara sobre las políticas de audio</>,
                          <>
                            Signalisation claire concernant les politiques
                            relatives à l'enregistrement audio
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              각
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조절
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장치가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표시됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Feedback mechanisms for member input</>,
                          <>
                            Mecanismos de retroalimentación para la
                            participación de los miembros
                          </>,
                          <>
                            Mécanismes de rétroaction pour la contribution des
                            membres
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              회원
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              의견
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수렴을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              메커니즘
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Headphone-friendly seating options</>,
                          <>
                            Opciones de asientos aptas para usar con auriculares
                          </>,
                          <>
                            Options de sièges adaptées à l'utilisation
                            d'écouteurs
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              헤드폰
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              적합한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              좌석
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              옵션
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
              </section>
              <hr className={classes.threeDotDivisor} />
              <section id={'remote-work-music'}>
                <h3 className={classes.subSection}>
                  {translate(
                    <>Remote Work: Personalizing Your Home Office Soundtrack</>,
                    <>
                      Trabajo a distancia: Personalizando la banda sonora de tu
                      oficina en casa
                    </>,
                    <>
                      Télétravail&nbsp;: Personnaliser la bande sonore de son
                      bureau à domicile
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재택근무:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        나만의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        홈
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오피스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사운드트랙
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        만들기
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Remote work presents entirely different opportunities for
                      mental wellness through sound. Without office distractions
                      or colleague preferences to consider, remote workers can
                      optimize their audio environment completely.
                    </>,
                    <>
                      El trabajo a distancia ofrece oportunidades completamente
                      diferentes para el bienestar mental a través del sonido.
                      Sin las distracciones de la oficina ni las preferencias de
                      los compañeros de trabajo, los trabajadores remotos pueden
                      optimizar su entorno sonoro por completo.
                    </>,
                    <>
                      Le télétravail offre des opportunités totalement
                      différentes en matière de bien-être mental grâce au son.
                      Sans les distractions du bureau ni les préférences des
                      collègues à prendre en compte, les télétravailleurs
                      peuvent optimiser entièrement leur environnement sonore.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        원격
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        근무는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소리를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        통해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        정신
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        건강을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        증진할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        완전히
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        새로운
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기회를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        제공합니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사무실의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        요소나
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        동료의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        취향을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고려할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        필요
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        없이,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        원격
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        근무자는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        자신의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오디오
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        환경을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        완벽하게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        최적화할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Interestingly, research reveals a fascinating pattern:
                      despite productivity expert advice to choose classical or
                      ambient music, remote workers overwhelmingly prefer pop,
                      dance, and rock. Analysis of thousands of work-from-home
                      playlists shows that these energetic genres dominate.
                    </>,
                    <>
                      Curiosamente, las investigaciones revelan un patrón
                      fascinante: a pesar de los consejos de los expertos en
                      productividad que recomiendan escoger música clásica o
                      ambiental, los trabajadores a distancia prefieren
                      mayoritariamente el pop, la música electrónica y el rock.
                      El análisis de miles de listas de reproducción para
                      trabajar desde casa muestra que estos géneros enérgicos
                      son los predominantes.
                    </>,
                    <>
                      Il est intéressant de constater que les recherches
                      révèlent une tendance fascinante&nbsp;: malgré les
                      conseils des experts en productivité qui recommandent
                      d'écouter de la musique classique ou d'ambiance, les
                      télétravailleurs privilégient massivement la pop, la
                      musique électronique et le rock. L'analyse de milliers de
                      listes de lecture utilisées en télétravail montre que ces
                      genres dynamiques sont largement majoritaires.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흥미롭게도,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        연구
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        결과는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        놀라운
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        패턴을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        보여줍니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        생산성
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전문가들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        클래식이나
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        앰비언트
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        추천하지만,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        재택근무자들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        압도적으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        팝,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        댄스,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        록
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선호하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        나타났습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수천
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        개의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        재택근무
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        플레이리스트를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        분석한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        결과,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이러한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        활기찬
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        장르의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        대부분을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        차지하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        드러났습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Why this matters: Remote workers have proven themselves
                      more productive than office workers on average. Their
                      music choices reflect authentic personal preference and
                      enjoyment rather than following “should” advice&nbsp;— and
                      the results speak for themselves.
                    </>,
                    <>
                      Por qué esto es importante: Los trabajadores remotos han
                      demostrado ser, en promedio, más productivos que los
                      trabajadores de oficina. Sus elecciones musicales reflejan
                      auténticas preferencias y gustos personales, en lugar de
                      seguir consejos preestablecidos, y los resultados hablan
                      por sí solos.
                    </>,
                    <>
                      Pourquoi c'est important&nbsp;: Les télétravailleurs se
                      sont avérés, en moyenne, plus productifs que les employés
                      de bureau. Leurs choix musicaux reflètent leurs
                      préférences et leurs goûts personnels, plutôt que de
                      suivre des conseils préconçus, et les résultats parlent
                      d'eux-mêmes.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        왜
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이것이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        중요할까요?
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        원격
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        근무자들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        평균적으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사무실
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        근무자보다
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        생산적이라는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        입증되었습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        그들의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선택은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        "해야
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        한다"는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        조언을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        따르는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        아니라
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        진정한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        개인적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        취향과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        즐거움을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        반영하며,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        그
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        결과는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스스로를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        증명합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'remote-work-daily-audio-guide'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>The Remote Work Daily Audio Journey</>,
                      <>El viaje de audio diario del trabajo remoto</>,
                      <>Le parcours audio quotidien du télétravail</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          원격
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          근무자를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일일
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오디오
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          여정
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Structure your audio environment to mirror your energy
                        and focus needs throughout the day:
                      </>,
                      <>
                        Estructura tu entorno de audio para que refleje tus
                        necesidades de energía y concentración a lo largo del
                        día:
                      </>,
                      <>
                        Structurez votre environnement sonore de manière à ce
                        qu'il reflète vos besoins en énergie et en concentration
                        tout au long de la journée&nbsp;:
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          하루
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          종일
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          에너지와
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중력
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수준에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞춰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오디오
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구성하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'remote-work-mornings'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Morning Activation (8am-10am): 85–100&nbsp;BPM</>,
                        <>
                          Activación matutina (8:00 a 10:00): 85-100 latidos por
                          minuto
                        </>,
                        <>Activation matinale (8h-10h)&nbsp;: 85-100 BPM</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활동
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            8시~오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            10시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            85~100
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Start your workday with energizing music that signals
                          to your brain “it’s work time.” This ritual becomes
                          especially crucial when working from home, where
                          boundaries between personal and professional life
                          blur.
                        </>,
                        <>
                          Comienza tu jornada laboral con música energizante que
                          le indique a tu cerebro que “es hora de trabajar”.
                          Este ritual se vuelve especialmente crucial cuando se
                          trabaja desde casa, donde los límites entre la vida
                          personal y profesional se difuminan.
                        </>,
                        <>
                          Commencez votre journée de travail avec une musique
                          énergisante qui signale à votre cerveau que
                          «&nbsp;c'est l'heure du travail&nbsp;». Ce rituel
                          devient particulièrement crucial lorsque l'on
                          travaille à domicile, où les frontières entre vie
                          personnelle et vie professionnelle s'estompent.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            활기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            넘치는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하루
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업무를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시작하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            뇌에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            "이제
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            일할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간이야"라는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            신호를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보내세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이러한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            습관은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개인적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            삶과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직장
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            생활의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경계가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모호해지기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            쉬운
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재택근무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            환경에서
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특히
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중요합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Choose upbeat instrumental or favorite songs that
                          boost mood without requiring attention. This music
                          should make you want to tackle your task list, not
                          dance or sing along.
                        </>,
                        <>
                          Elige música instrumental animada o tus canciones
                          favoritas que te levanten el ánimo sin que requieran
                          tu atención. Esta música debe motivarte a abordar tu
                          lista de tareas, no a bailar ni a cantar.
                        </>,
                        <>
                          Choisissez des morceaux instrumentaux entraînants ou
                          des chansons que vous aimez et qui vous mettent de
                          bonne humeur, sans pour autant requérir toute votre
                          attention. Cette musique doit vous donner envie de
                          vous attaquer à votre liste de tâches, et non de
                          danser ou de chanter.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            기분을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            해주는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경쾌한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            연주곡이나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋아하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노래를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            선택하되,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            주의를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            필요가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            곡이어야
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            합니다.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            당신이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            일
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            목록을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            처리하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            싶게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            만들어야
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하며,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            춤을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추거나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            따라
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            부르고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            싶게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            만들어서는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            안
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            됩니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Recommended:
                          </span>{' '}
                          Upbeat indie rock instrumentals, motivational
                          electronic music, morning jazz compilations
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recomendado:
                          </span>{' '}
                          Música instrumental indie rock animada, música
                          electrónica motivadora, recopilaciones de jazz para la
                          mañana
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recommandé&nbsp;:
                          </span>{' '}
                          Morceaux instrumentaux indie rock entraînants, musique
                          électronique motivante, compilations de jazz pour le
                          matin
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추천
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활기찬
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            인디
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            록
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            연주곡,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            동기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            부여를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            주는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            일렉트로닉
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            아침에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            듣기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재즈
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모음곡
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'remote-work-deep-focus-time'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Deep Focus Blocks (10am-12pm): 60–75&nbsp;BPM</>,
                        <>
                          Bloques de concentración profunda (10:00-12:00):
                          60-75&nbsp;BPM
                        </>,
                        <>
                          Périodes de concentration intense (10h-12h)&nbsp;: 60
                          à 75 BPM
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            집중력
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            향상
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간대
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            10시~오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            12시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            60~75
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Once energized and settled into work mode, switch to
                          slower instrumental music supporting sustained
                          concentration. This is prime time for your most
                          cognitively demanding tasks.
                        </>,
                        <>
                          Una vez que te sientas con energía y concentrado en el
                          trabajo, cambia a música instrumental más lenta que
                          favorezca la concentración sostenida. Este es el
                          momento ideal para realizar las tareas que requieren
                          mayor esfuerzo cognitivo.
                        </>,
                        <>
                          Une fois que vous êtes plein d'énergie et concentré
                          sur votre travail, passez à une musique instrumentale
                          plus douce qui favorise une concentration soutenue.
                          C'est le moment idéal pour accomplir vos tâches les
                          plus exigeantes sur le plan cognitif.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            일단
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활력을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            되찾고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모드에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            돌입했다면,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중력을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            데
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            도움이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            되는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            잔잔한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            연주곡으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            바꿔보세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이때가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            가장
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            인지적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중력을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            요하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            작업을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하기에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            최적의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간입니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Recommended:
                          </span>{' '}
                          Classical focus playlists, ambient soundscapes, lo-fi
                          hip hop, video game soundtracks
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recomendado:
                          </span>{' '}
                          Listas de reproducción de música clásica, paisajes
                          sonoros ambientales, hip hop lo-fi, bandas sonoras de
                          videojuegos
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recommandé&nbsp;:
                          </span>{' '}
                          Listes de lecture pour la concentration (musique
                          classique), ambiances sonores, hip-hop lo-fi, bandes
                          originales de jeux vidéo
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추천:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            클래식
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중심
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            앰비언트
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사운드스케이프,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            로파이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            힙합,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            비디오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            게임
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사운드트랙
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'remote-work-midday-recharge'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Midday Recharge (12pm-1pm): Personal Choice</>,
                        <>
                          Recarga de energía al mediodía (12:00-13:00): Elección
                          personal
                        </>,
                        <>
                          Pause déjeuner (12h00-13h00)&nbsp;: Choix personnel
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            점심시간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재충전
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            12시~오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            1시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            선택
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Take an actual lunch break with music you genuinely
                          enjoy. Lyrics are fine here. This mental break helps
                          prevent afternoon burnout.
                        </>,
                        <>
                          Tómate un verdadero descanso para almorzar con música
                          que realmente disfrutes. Las letras de las canciones
                          no son un problema. Este descanso mental ayuda a
                          prevenir el agotamiento de la tarde.
                        </>,
                        <>
                          Prenez une vraie pause déjeuner en écoutant de la
                          musique que vous appréciez vraiment. Les paroles ne
                          posent aucun problème. Cette pause mentale permet de
                          prévenir l'épuisement en fin d'après-midi.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            점심시간에는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            실제로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            휴식을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            취하면서
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            진심으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋아하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            들어보세요. 가사가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악도
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            괜찮습니다.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이러한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정신적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            휴식은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            오후에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            찾아오는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피로감을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            예방하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            데
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            도움이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            됩니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Recommended:
                          </span>{' '}
                          Whatever makes you happy&nbsp;— podcasts, favorite
                          songs, silence if that recharges you
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recomendado:
                          </span>{' '}
                          Lo que te haga feliz: podcasts, tus canciones
                          favoritas, silencio si eso te recarga las energías.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recommandé&nbsp;:
                          </span>{' '}
                          Tout ce qui vous rend heureux&nbsp;: des podcasts, vos
                          chansons préférées, du silence si cela vous ressource.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추천:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            당신을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            행복하게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            해주는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            것
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            —
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            팟캐스트,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋아하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노래,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            아니면
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재충전에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            도움이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            된다면
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조용한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간도
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'remote-work-afternoon-productivity'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Afternoon Productivity (1pm-3pm): 70–90&nbsp;BPM</>,
                        <>
                          Productividad por la tarde (13:00-15:00): 70-90
                          pulsaciones por minuto
                        </>,
                        <>
                          Productivité de l'après-midi (13h-15h)&nbsp;: 70 à 90
                          BPM
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            생산성
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            1시~오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            3시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            70~90
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Combat the post-lunch energy dip with moderate-tempo
                          music that maintains focus without inducing
                          drowsiness.
                        </>,
                        <>
                          Combate la bajada de energía después del almuerzo con
                          música de ritmo moderado que te ayude a mantener la
                          concentración sin provocar somnolencia.
                        </>,
                        <>
                          Combattez la baisse d'énergie de l'après-midi avec de
                          la musique à tempo modéré qui favorise la
                          concentration sans provoquer de somnolence.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            점심
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            식사
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            찾아오는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            졸음을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            물리치고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중력을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지하려면
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적당한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            속도의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            들어보세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Recommended:
                          </span>{' '}
                          Acoustic covers, chill electronic, jazz fusion
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recomendado:
                          </span>{' '}
                          Versiones acústicas, música electrónica relajante,
                          jazz fusión
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recommandé&nbsp;:
                          </span>{' '}
                          Reprises acoustiques, musique électronique relaxante,
                          jazz fusion
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추천
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장르:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어쿠스틱
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            커버곡,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            일렉트로닉
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재즈
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            퓨전
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'remote-work-energy-recovery'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Energy Dip Recovery (3pm-4pm): 80–95 BPM</>,
                        <>
                          Recuperación tras la bajada de energía (15:00-16:00):
                          80-95 pulsaciones por minuto.
                        </>,
                        <>
                          Récupération après la baisse d'énergie
                          (15h-16h)&nbsp;: 80-95 battements par minute
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            에너지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            저하
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            회복
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            3시~4시):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            80~95
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          This is when most people experience significant energy
                          crashes. Strategically use moderate-to-upbeat
                          favorites (even with lyrics if doing routine tasks) to
                          push through.
                        </>,
                        <>
                          En este momento es cuando la mayoría de las personas
                          experimentan una importante caída de energía. Utiliza
                          estratégicamente canciones favoritas con un ritmo
                          moderado o animado (incluso con letra si estás
                          realizando tareas rutinarias) para superar este bajón.
                        </>,
                        <>
                          C'est à ce moment-là que la plupart des gens
                          ressentent une baisse d'énergie importante. Utilisez
                          judicieusement des morceaux préférés au rythme modéré
                          à entraînant (même avec des paroles si vous effectuez
                          des tâches routinières) pour vous remotiver.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간대는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대부분의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사람들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            심각한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            에너지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            저하를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경험하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때입니다.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이럴
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적당히
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활기찬
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악(일상적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            작업을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            가사가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악도
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            괜찮습니다)을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전략적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활용하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            힘든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            극복하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Recommended:
                          </span>{' '}
                          Personal favorite songs, familiar upbeat tracks
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recomendado:
                          </span>{' '}
                          Canciones favoritas personales, temas animados y
                          conocidos.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recommandé&nbsp;:
                          </span>{' '}
                          Mes chansons préférées, des morceaux entraînants et
                          familiers.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추천:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개인적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋아하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노래,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            익숙하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            신나는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            곡들
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'remote-work-end-of-workday'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>End-of-Day Completion (4pm-6pm): 75–85&nbsp;BPM</>,
                        <>
                          Finalización de la jornada (16:00-18:00): 75-85 lpm
                        </>,
                        <>
                          Achèvement en fin de journée (16h-18h)&nbsp;: 75-85
                          BPM
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            업무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            종료
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간대
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            4시~6시)
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            완료율:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            75~85
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Maintain steady energy for final task completion while
                          beginning the mental transition from work to personal
                          time.
                        </>,
                        <>
                          Mantén un nivel de energía constante para completar
                          las tareas finales mientras comienzas la transición
                          mental del trabajo al tiempo personal.
                        </>,
                        <>
                          Maintenez un niveau d'énergie constant pour mener à
                          bien les dernières tâches tout en commençant la
                          transition mentale entre le travail et votre temps
                          personnel.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            마지막
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업무를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            완료하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            동안
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            꾸준한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            에너지를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지하고,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            동시에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모드에서
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개인적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모드로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정신적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전환을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시작하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Recommended:
                          </span>{' '}
                          Laid-back acoustic, indie folk, downtempo
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recomendado:
                          </span>{' '}
                          Música acústica relajada, indie folk, downtempo
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recommandé&nbsp;:
                          </span>{' '}
                          Musique acoustique décontractée, folk indépendant,
                          downtempo
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추천
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장르:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어쿠스틱
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            인디
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            포크,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            다운템포
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'remote-work-back-to-personal-life'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Work-Life Transition (6pm+): Calming Acoustic</>,
                        <>
                          Transición entre el trabajo y la vida personal (a
                          partir de las 18:00): Música acústica relajante
                        </>,
                        <>
                          Transition entre le travail et la vie personnelle (à
                          partir de 18h)&nbsp;: Musique acoustique apaisante
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            일과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            삶의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전환
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (오후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            6시
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이후):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어쿠스틱
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Use deliberately relaxing music to signal work
                          completion and help your brain shift into relaxation
                          mode. This ritual prevents work thoughts from invading
                          evening hours.
                        </>,
                        <>
                          Utiliza música relajante de forma intencionada para
                          indicar la finalización del trabajo y ayudar a tu
                          cerebro a entrar en modo de relajación. Este ritual
                          evita que los pensamientos relacionados con el trabajo
                          invadan las horas de la noche.
                        </>,
                        <>
                          Utilisez une musique délibérément relaxante pour
                          signaler la fin du travail et aider votre cerveau à
                          passer en mode détente. Ce rituel empêche les pensées
                          liées au travail d'envahir vos soirées.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            일부러
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            틀어
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            작업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            완료를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            알리고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            뇌가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            휴식
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모드로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전환되도록
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            도와주세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이러한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            의식은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관련
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            생각이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            저녁
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간까지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이어지는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            것을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            방지해
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            줍니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Recommended:
                          </span>{' '}
                          Acoustic singer-songwriters, ambient piano, nature
                          sounds
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recomendado:
                          </span>{' '}
                          Cantautores acústicos, piano ambiental, sonidos de la
                          naturaleza
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recommandé&nbsp;:
                          </span>{' '}
                          Auteurs-compositeurs-interprètes acoustiques, piano
                          d'ambiance, sons de la nature
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            추천
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장르:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어쿠스틱
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            싱어송라이터,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            앰비언트
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피아노
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자연의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소리
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                </section>
                <section id={'playlists-for-remote-teams'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>The Collaborative Playlist Strategy for Remote Teams</>,
                      <>
                        Estrategia de listas de reproducción colaborativas para
                        equipos remotos
                      </>,
                      <>
                        La stratégie de listes de lecture collaboratives pour
                        les équipes travaillant à distance
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          원격
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          근무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          팀을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          협업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Distributed teams are discovering an unexpected benefit:
                        shared music playlists create connection despite
                        physical distance.
                      </>,
                      <>
                        Los equipos distribuidos están descubriendo un beneficio
                        inesperado: las listas de reproducción de música
                        compartidas crean conexión a pesar de la distancia
                        física.
                      </>,
                      <>
                        Les équipes réparties sur différents sites découvrent un
                        avantage inattendu&nbsp;: les listes de lecture
                        musicales partagées créent du lien malgré la distance
                        physique.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분산된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          팀들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          예상치
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          못한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이점을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          발견하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다. 물리적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          거리에도
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          불구하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공유
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          연결감을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          형성한다는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'remote-team-playlists-how-to'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>How it works:</>,
                        <>Cómo funciona:</>,
                        <>Comment ça marche&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          ></span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Create a team Spotify or Apple Music collaborative
                          playlist where everyone contributes favorite work
                          tracks. This serves multiple purposes:
                        </>,
                        <>
                          Crea una lista de reproducción colaborativa en Spotify
                          o Apple Music donde todos aporten sus canciones
                          favoritas para trabajar. Esto tiene múltiples
                          ventajas:
                        </>,
                        <>
                          Créez une playlist collaborative sur Spotify ou Apple
                          Music où chacun peut ajouter ses morceaux préférés
                          pour le travail. Cela présente plusieurs
                          avantages&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            Spotify
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            또는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            Apple
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            Music에서
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            팀원이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            좋아하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관련
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추가할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공동
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            만들어
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            여러
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            가지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이점을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제공합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Cultural connection:
                            </span>{' '}
                            Music reveals personality and creates conversation
                            starters during virtual meetings.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Conexión cultural:
                            </span>{' '}
                            La música revela la personalidad y genera temas de
                            conversación durante las reuniones virtuales.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Connexion culturelle&nbsp;:
                            </span>{' '}
                            La musique révèle la personnalité et permet
                            d'engager la conversation lors des réunions
                            virtuelles.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              문화적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              연결:
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개인의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              성격을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              드러내고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가상
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회의에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대화를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시작하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              데
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              도움이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Discovery and variety:
                            </span>{' '}
                            Team members discover new artists and genres they
                            might enjoy.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Descubrimiento y variedad:
                            </span>{' '}
                            Los miembros del equipo descubren nuevos artistas y
                            géneros que podrían gustarles.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Découverte et diversité&nbsp;:
                            </span>{' '}
                            Les membres de l'équipe découvrent de nouveaux
                            artistes et genres qu'ils pourraient apprécier.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              발견과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양성:
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀원들은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              자신이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              좋아할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              새로운
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아티스트와
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장르를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              발견할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Shared experience:
                            </span>{' '}
                            When team members mention “I’m listening to the team
                            playlist today,” it creates a sense of togetherness
                            despite remote work.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Experiencia compartida:
                            </span>{' '}
                            Cuando los miembros del equipo mencionan "Hoy estoy
                            escuchando la lista de reproducción del equipo", se
                            crea un sentimiento de unidad a pesar del trabajo a
                            distancia.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Expérience partagée&nbsp;:
                            </span>{' '}
                            Lorsque les membres de l'équipe mentionnent «
                            J'écoute la playlist de l'équipe aujourd'hui », cela
                            crée un sentiment de cohésion malgré le télétravail.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              공유된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경험:
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀원들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              "오늘
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              듣고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있어요"라고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              말할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              때,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              원격
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              근무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              환경에서도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소속감을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              느끼게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              해줍니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Inclusion and diversity:
                            </span>{' '}
                            Music preferences reflect cultural backgrounds,
                            creating natural opportunities for cross-cultural
                            appreciation.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Inclusión y diversidad:
                            </span>{' '}
                            Las preferencias musicales reflejan los orígenes
                            culturales, creando oportunidades naturales para la
                            apreciación intercultural.
                          </>,
                          <>
                            <span
                              className={
                                classes.spanBolder + ' ' + classes.spanItalic
                              }
                            >
                              Inclusion et diversité&nbsp;:
                            </span>{' '}
                            Les préférences musicales reflètent les origines
                            culturelles, créant ainsi des opportunités
                            naturelles d'appréciation interculturelle.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              포용성과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양성:
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              취향은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              문화적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              배경을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              반영하며,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              이는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              문화
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              간
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              이해와
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              감상을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              자연스러운
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기회를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만들어냅니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'implementation-for-remote-teams'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Implementation tips:</>,
                        <>Consejos para la implementación:</>,
                        <>Conseils de mise en œuvre&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            구현
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            팁:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Set loose guidelines (work-appropriate lyrics,
                            generally instrumental preferred)
                          </>,
                          <>
                            Se establecen pautas generales (letras apropiadas
                            para el entorno laboral, se prefiere música
                            instrumental).
                          </>,
                          <>
                            Établissez des directives générales (paroles
                            appropriées au milieu professionnel, musique
                            instrumentale généralement préférée).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              느슨한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가이드라인을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설정하세요
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (직장
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              환경에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              적합한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가사,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일반적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              연주곡
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선호).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Rotate “playlist curator of the week” to keep it
                            fresh
                          </>,
                          <>
                            Rota el puesto de "curador de listas de reproducción
                            de la semana" para mantenerlo actualizado.
                          </>,
                          <>
                            Faites tourner le titre de «&nbsp;curateur de
                            playlist de la semaine&nbsp;» pour maintenir
                            l'intérêt.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              "이번
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              추천
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              큐레이터"를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              정기적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              바꿔서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              신선함을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              유지하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Create separate playlists for different moods
                            (focus, energy, chill)
                          </>,
                          <>
                            Crea listas de reproducción separadas para
                            diferentes estados de ánimo (concentración, energía,
                            relajación).
                          </>,
                          <>
                            Créez des listes de lecture distinctes pour
                            différentes ambiances (concentration, énergie,
                            détente).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              다양한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분위기(집중,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              활력,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              휴식)에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              맞춰
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              별도의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재생
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목록을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만드세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Share favorite tracks during team meetings as an
                            icebreaker
                          </>,
                          <>
                            Compartan sus canciones favoritas durante las
                            reuniones de equipo como una forma de romper el
                            hielo.
                          </>,
                          <>
                            Partagez vos morceaux préférés lors des réunions
                            d'équipe pour briser la glace.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              팀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회의에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분위기를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부드럽게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              하기
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              좋아하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공유해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              보세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
              </section>
              <hr className={classes.threeDotDivisor} />
              <section id={'four-step-workplace-music-plan'}>
                <h3 className={classes.subSection}>
                  {translate(
                    <>Your 4-Step Workplace Music Implementation Plan</>,
                    <>
                      Su plan de implementación de música en el lugar de trabajo
                      en 4 pasos
                    </>,
                    <>
                      Votre plan de mise en œuvre de la musique au travail en 4
                      étapes
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        내
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        도입을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        위한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        4단계
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실행
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        계획
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Moving from research to reality requires structured
                      planning. Here’s your proven framework.
                    </>,
                    <>
                      Para pasar de la investigación a la práctica se necesita
                      una planificación estructurada. Aquí tienes un marco de
                      trabajo probado.
                    </>,
                    <>
                      Passer de la recherche à la réalité nécessite une
                      planification structurée. Voici un cadre de travail
                      éprouvé.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        단계에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실제
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        적용
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        단계로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        나아가려면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        체계적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        계획이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        필요합니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        여기
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        검증된
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        프레임워크가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                {secondArticleImage}
                <section id={'step-1-assess-team-profile'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Step 1: Assess Your Team’s Work Profile and Preferences
                      </>,
                      <>
                        Paso 1: Evalúe el perfil de trabajo y las preferencias
                        de su equipo.
                      </>,
                      <>
                        Étape 1&nbsp;: Évaluez le profil de travail et les
                        préférences de votre équipe.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          1단계:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          팀의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로필과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선호도를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          평가하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Before selecting a single playlist or installing any
                        equipment, deeply understand your workplace reality.
                      </>,
                      <>
                        Antes de seleccionar una lista de reproducción o
                        instalar cualquier equipo, comprenda a fondo la realidad
                        de su lugar de trabajo.
                      </>,
                      <>
                        Avant de sélectionner une seule liste de lecture ou
                        d'installer quelque équipement que ce soit, comprenez
                        parfaitement la réalité de votre lieu de travail.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          단
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하나의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선택하거나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          장비를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          설치하기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전에,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직장의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          현실을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          철저히
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이해해야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'step-1-map-task-distribution'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>Map task distribution across your organization:</>,
                        <>
                          Mapee la distribución de tareas en toda su
                          organización:
                        </>,
                        <>
                          Cartographiez la répartition des tâches au sein de
                          votre organisation&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            조직
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전체에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            걸쳐
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            작업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분배
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            현황을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            파악하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            What percentage of work requires deep, uninterrupted
                            focus? (Analysis, writing, coding, detailed review)
                          </>,
                          <>
                            ¿Qué porcentaje del trabajo requiere una
                            concentración profunda e ininterrumpida? (Análisis,
                            redacción, programación, revisión detallada)
                          </>,
                          <>
                            Quel pourcentage du travail nécessite une
                            concentration intense et ininterrompue&nbsp;?
                            (Analyse, rédaction, programmation, examen détaillé)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              얼마나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              많은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부분이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              깊고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방해받지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중력을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              필요로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              합니까?
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (분석,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              글쓰기,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              코딩,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              상세
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              검토
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              등)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            How much time involves collaborative activities?
                            (Meetings, brainstorms, pair work, team projects)
                          </>,
                          <>
                            ¿Cuánto tiempo se dedica a actividades
                            colaborativas? (Reuniones, sesiones de lluvia de
                            ideas, trabajo en parejas, proyectos en equipo)
                          </>,
                          <>
                            Combien de temps est consacré aux activités
                            collaboratives&nbsp;? (Réunions, séances de
                            brainstorming, travail en binôme, projets d'équipe)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              협업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              활동에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              얼마나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              많은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소요됩니까?
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (회의,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              브레인스토밍,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              짝
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              활동,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              프로젝트
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              등)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            What proportion is repetitive versus creative? (Data
                            entry vs. design work, routine admin vs. strategic
                            planning)
                          </>,
                          <>
                            ¿Qué proporción es trabajo repetitivo frente a
                            trabajo creativo? (Entrada de datos frente a trabajo
                            de diseño, tareas administrativas rutinarias frente
                            a planificación estratégica)
                          </>,
                          <>
                            Quelle est la proportion de tâches répétitives par
                            rapport aux tâches créatives&nbsp;? (Saisie de
                            données vs. travail de conception, tâches
                            administratives courantes vs. planification
                            stratégique)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              반복적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무와
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              창의적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              비율은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              얼마나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              되나요?
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (데이터
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              입력
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              vs.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              디자인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작업,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일상적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관리
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              vs.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전략
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기획)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Are there customer-facing roles requiring different
                            audio environments? (sales calls, client meetings,
                            customer service)
                          </>,
                          <>
                            ¿Existen puestos de atención al cliente que
                            requieran diferentes entornos de audio? (llamadas de
                            ventas, reuniones con clientes, servicio al cliente)
                          </>,
                          <>
                            Existe-t-il des postes en contact direct avec la
                            clientèle nécessitant des environnements audio
                            différents&nbsp;? (appels commerciaux, réunions avec
                            les clients, service client)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              고객
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              응대
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              역할을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수행하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              데
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있어
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              오디오
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              환경이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              필요한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경우가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니까?
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (영업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전화,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              고객
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회의,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              고객
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              서비스
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              등)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'step-1-survey-employees'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>Survey employees anonymously and thoroughly:</>,
                        <>
                          Encuestar a las empleadas de forma anónima y
                          exhaustiva:
                        </>,
                        <>
                          Sondez les employés de manière anonyme et
                          approfondie&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            직원들을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대상으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            익명으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설문조사를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            철저하게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            실시하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Do they currently use personal headphones? If so,
                            what do they listen to?
                          </>,
                          <>
                            ¿Utilizan actualmente auriculares personales? En
                            caso afirmativo, ¿qué escuchan?
                          </>,
                          <>
                            Utilisent-ils actuellement des écouteurs personnels
                            ? Si oui, qu'écoutent-ils&nbsp;?
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              그들은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              현재
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개인용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              헤드폰을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있나요?
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              그렇다면
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              무엇을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              듣고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있나요?
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            What environmental factors distract them most?
                            (Conversations, phones, HVAC noise, complete
                            silence)
                          </>,
                          <>
                            ¿Qué factores ambientales les distraen más?
                            (Conversaciones, teléfonos, ruido del sistema de
                            climatización, silencio absoluto)
                          </>,
                          <>
                            Quels sont les facteurs environnementaux qui les
                            distraient le plus&nbsp;? (Conversations,
                            téléphones, bruit de la climatisation, silence
                            complet)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              어떤
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              환경적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              요인들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              그들의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중력을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가장
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              많이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방해하나요?
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (대화,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              휴대폰,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              냉난방기
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              완전한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              침묵)
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              현재
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              헤드폰을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있나요?
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              그렇다면
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              무엇을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              듣고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있나요?
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Would they prefer office-wide music, personal
                            headphone choice, or designated quiet zones?
                          </>,
                          <>
                            ¿Preferirían música ambiental en toda la oficina, la
                            posibilidad de escuchar música con auriculares
                            individuales o zonas designadas para el silencio?
                          </>,
                          <>
                            Préféreraient-ils de la musique diffusée dans tout
                            le bureau, la possibilité d'écouter de la musique
                            avec des écouteurs personnels, ou des zones calmes
                            désignées&nbsp;?
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              그들은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사무실
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전체에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              나오는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              것을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선호할까요,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아니면
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              헤드폰을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              것을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선호할까요,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아니면
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조용한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지정하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              것을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선호할까요?
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            What music genres do they find most conducive to
                            focus?
                          </>,
                          <>
                            ¿Qué géneros musicales consideran que son los más
                            propicios para la concentración?
                          </>,
                          <>
                            Quels genres musicaux trouvent-ils les plus propices
                            à la concentration&nbsp;?
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              그들은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              어떤
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장르가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중력
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              향상에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가장
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              도움이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              된다고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              생각할까요?
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Are there genres or sounds they find especially
                            distracting?
                          </>,
                          <>
                            ¿Hay algún género musical o tipo de sonido que les
                            resulte especialmente molesto?
                          </>,
                          <>
                            Existe-t-il des genres musicaux ou des sons qu'ils
                            trouvent particulièrement gênants&nbsp;?
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              그들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              특히
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중력을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방해한다고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              생각하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장르나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소리가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있나요?
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'step-1-analyze-physical-space'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>Analyze physical space:</>,
                        <>Analizar el espacio físico:</>,
                        <>Analyser l'espace physique&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            물리적
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분석:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Map your office layout with attention to acoustic
                          properties. Where do sounds carry? Which areas already
                          feel isolated? Where do teams naturally gather?
                        </>,
                        <>
                          Diseñe la distribución de su oficina prestando
                          atención a las propiedades acústicas. ¿Por dónde se
                          propaga el sonido? ¿Qué áreas ya se perciben como
                          aisladas? ¿Dónde se reúnen los equipos de forma
                          natural?
                        </>,
                        <>
                          Cartographiez l'aménagement de vos bureaux en tenant
                          compte de leurs propriétés acoustiques. Où le son se
                          propage-t-il&nbsp;? Quelles zones sont déjà
                          naturellement isolées&nbsp;? Où les équipes se
                          réunissent-elles spontanément&nbsp;?
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            사무실
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            레이아웃을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설계할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음향
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특성을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고려하세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소리가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어디까지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전달되는지,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어떤
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이미
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소음으로부터
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            차단되어
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는지,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            팀원들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자연스럽게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            모이는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            곳은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어디인지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            등을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            파악하십시오.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Identify natural zone boundaries&nbsp;— structural
                          walls, furniture arrangements, distance between desks.
                        </>,
                        <>
                          Identifique los límites de las zonas naturales:
                          paredes estructurales, disposición de los muebles,
                          distancia entre los escritorios.
                        </>,
                        <>
                          Identifier les limites des zones naturelles&nbsp;:
                          murs porteurs, agencement du mobilier, distance entre
                          les bureaux.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            자연스러운
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경계를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            파악하세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구조벽,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            가구
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            배치,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            책상
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사이의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            거리
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            등을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고려하십시오.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        This assessment prevents the most common implementation
                        mistake: forcing a one-size-fits-all solution that
                        satisfies no one.
                      </>,
                      <>
                        Esta evaluación previene el error de implementación más
                        común: imponer una solución única que no satisface a
                        nadie.
                      </>,
                      <>
                        Cette évaluation permet d'éviter l'erreur de mise en
                        œuvre la plus courante&nbsp;: imposer une solution
                        unique qui ne satisfait personne.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          평가를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          흔한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구현
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오류,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          즉
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          누구에게도
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          만족스럽지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          않은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          획일적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          솔루션을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          강요하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실수를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          방지할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'step-2-create-strategic-music-zones'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Step 2: Create Strategic Audio Zones Aligned with Work
                        Modes
                      </>,
                      <>
                        Paso 2: Crear zonas de audio estratégicas alineadas con
                        los modos de trabajo.
                      </>,
                      <>
                        Étape 2&nbsp;: Créer des zones audio stratégiques
                        alignées sur les modes de travail.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          2단계:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          방식에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞춰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오디오
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          설정하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Using your assessment data, design distinct audio
                        environments serving different cognitive demands.
                      </>,
                      <>
                        Utilizando los datos de su evaluación, diseñe entornos
                        de audio distintos que satisfagan diferentes demandas
                        cognitivas.
                      </>,
                      <>
                        À partir des données de votre évaluation, concevez des
                        environnements sonores distincts répondant à différentes
                        exigences cognitives.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          평가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          데이터를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활용하여
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다양한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인지적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요구에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차별화된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오디오
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          설계하십시오.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'step-2-offices-with-structure-division'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>For open offices without structural divisions:</>,
                        <>
                          Para oficinas diáfanas sin divisiones estructurales:
                        </>,
                        <>
                          Pour les bureaux ouverts sans cloisons
                          structurelles&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            구조적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            칸막이가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개방형
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사무실의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경우:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Use strategic furniture placement, acoustic panels,
                            and visual markers to create perceived zones even
                            without walls.
                          </>,
                          <>
                            Utilice una disposición estratégica del mobiliario,
                            paneles acústicos y marcadores visuales para crear
                            zonas diferenciadas incluso sin paredes.
                          </>,
                          <>
                            Utilisez un agencement stratégique du mobilier, des
                            panneaux acoustiques et des repères visuels pour
                            créer des zones distinctes, même sans cloisons.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              가구의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전략적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              배치,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              흡음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              패널,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시각적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표식
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              등을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              활용하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              벽이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              없더라도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구분하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              효과를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만들어낼
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Install focused sound masking systems in designated
                            deep work areas rather than playing music
                            throughout.
                          </>,
                          <>
                            Instale sistemas de enmascaramiento de sonido
                            focalizados en las áreas de trabajo que lo
                            requieran, en lugar de reproducir música en todo el
                            espacio.
                          </>,
                          <>
                            Installez des systèmes de masquage sonore ciblés
                            dans les zones de travail nécessitant une
                            concentration intense, plutôt que de diffuser de la
                            musique partout.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              전체
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              트는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대신,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지정된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중형
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              차단
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시스템을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설치하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Clearly communicate zone purposes through signage,
                            floor markings, and team agreements.
                          </>,
                          <>
                            Comunique claramente los propósitos de cada zona
                            mediante señalización, marcas en el suelo y acuerdos
                            de equipo.
                          </>,
                          <>
                            Communiquez clairement les objectifs de chaque zone
                            au moyen de panneaux de signalisation, de marquages
                            au sol et d'accords d'équipe.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              표지판,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              바닥
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표시
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              및
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              간
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              합의를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              통해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              각
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목적을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              명확하게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전달하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Consider overhead announcements or digital displays
                            indicating “Focus hours now in effect” for
                            time-based zone shifts.
                          </>,
                          <>
                            Considere los anuncios por megafonía o las pantallas
                            digitales que indiquen "Horario de concentración en
                            vigor" para los cambios de zona basados ​​en el
                            tiempo.
                          </>,
                          <>
                            Envisagez d'utiliser des annonces sonores ou des
                            écrans numériques indiquant «&nbsp;Période de
                            concentration en cours&nbsp;» pour les changements
                            de zone horaire.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              시간대별
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전환을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              "집중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              근무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              적용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              중"이라는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              안내
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방송이나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              디지털
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              디스플레이를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              활용하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              것을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              고려해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              보세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'step-2-for-small-offices'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          For smaller offices without room for multiple zones:
                        </>,
                        <>
                          Para oficinas más pequeñas que no disponen de espacio
                          para varias zonas:
                        </>,
                        <>
                          Pour les petits bureaux ne disposant pas de
                          suffisamment d'espace pour aménager plusieurs
                          zones&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            여러
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구역을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설치할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소규모
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사무실의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경우:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Implement time-based audio strategies. Designate
                            specific hours for silence (9am-11am for
                            deep&nbsp;work), moderate music (11am-1pm), and
                            personal choice (break&nbsp;times).
                          </>,
                          <>
                            Implementa estrategias de audio basadas en el
                            tiempo. Designa horarios específicos para el
                            silencio (de 9:00 a 11:00 para trabajo de
                            concentración), música moderada (de 11:00 a 13:00) y
                            elección personal (durante los descansos).
                          </>,
                          <>
                            Mettez en œuvre des stratégies audio basées sur le
                            temps. Définissez des plages horaires spécifiques
                            pour le silence (de 9h à 11h pour un travail de
                            concentration), pour une musique d'ambiance (de 11h
                            à 13h) et pour un choix personnel (pendant les
                            pauses).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              시간
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기반
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              오디오
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전략을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              실행하세요.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              특정
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              정해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조용한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간(오전
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              9시~11시
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              집중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간),
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              적당한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              듣는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간(오전
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              11시~오후
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              1시),
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              그리고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개인적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선택에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              듣는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간(휴식
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간)으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구분하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Poll the team weekly on preferred genres and create
                            rotating weekly playlists reflecting collective
                            preferences.
                          </>,
                          <>
                            Pregunte al equipo semanalmente sobre sus géneros
                            musicales preferidos y cree listas de reproducción
                            semanales rotativas que reflejen las preferencias
                            colectivas.
                          </>,
                          <>
                            Sondez l'équipe chaque semaine sur leurs genres
                            musicaux préférés et créez des listes de lecture
                            hebdomadaires qui changent régulièrement et
                            reflètent les préférences collectives.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              팀원들에게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              매주
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선호하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장르를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설문조사하고,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀원들의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              의견을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              반영하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              매주
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              바뀌는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만드세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Strongly encourage headphone use with clear policies
                            about when they’re appropriate.
                          </>,
                          <>
                            Se recomienda encarecidamente el uso de auriculares,
                            con políticas claras sobre cuándo son apropiados.
                          </>,
                          <>
                            Il est fortement recommandé d'utiliser des
                            écouteurs, avec des règles claires concernant les
                            situations où leur utilisation est appropriée.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              Strongly
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              encourage
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              headphone
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              use
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              with
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              clear
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              policies
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              about
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              when
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              they’re
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              appropriate.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'step-2-for-remote-teams'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>For distributed teams working remotely:</>,
                        <>
                          Para equipos distribuidos que trabajan de forma
                          remota:
                        </>,
                        <>
                          Pour les équipes distribuées travaillant à
                          distance&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            원격으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            근무하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분산된
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            팀을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            위해:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Provide curated playlists organized by work type
                            (focus, creative, energy, collaboration background).
                          </>,
                          <>
                            Ofrecemos listas de reproducción seleccionadas y
                            organizadas por tipo de trabajo (para concentrarse,
                            para la creatividad, para energizarse, para trabajar
                            en colaboración).
                          </>,
                          <>
                            Proposez des listes de lecture sélectionnées et
                            organisées par type de travail (concentration,
                            créativité, énergie, musique d'ambiance pour le
                            travail collaboratif).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              유형별로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              정리된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              엄선된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제공합니다
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (집중력
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              향상,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              창의력
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              증진,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              에너지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              충전,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              협업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              배경
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Create shared team playlists for connection and
                            cultural bonding.
                          </>,
                          <>
                            Crea listas de reproducción compartidas para el
                            equipo con el fin de fomentar la conexión y el
                            vínculo cultural.
                          </>,
                          <>
                            Créez des listes de lecture d'équipe partagées pour
                            favoriser les liens et la cohésion culturelle.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              팀원들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              함께
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              즐길
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공유
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만들어
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소통과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              문화적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              유대감을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              강화하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Host optional “focus sessions” where team members
                            work simultaneously with the same soundtrack,
                            creating virtual coworking.
                          </>,
                          <>
                            Organiza "sesiones de concentración" opcionales en
                            las que los miembros del equipo trabajan
                            simultáneamente con la misma banda sonora, creando
                            un espacio de trabajo conjunto virtual.
                          </>,
                          <>
                            Organisez des «&nbsp;séances de concentration&nbsp;»
                            facultatives où les membres de l'équipe travaillent
                            simultanément avec la même bande sonore, créant
                            ainsi un espace de coworking virtuel.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              팀원들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              같은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              배경
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              들으며
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              동시에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작업하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              "집중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              세션"을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선택적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              진행하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가상
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              협업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              환경을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조성하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'step-3-offer-personal-control'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Step 3: Offer Personal Control While Maintaining
                        Community
                      </>,
                      <>
                        Paso 3: Ofrecer control personal manteniendo al mismo
                        tiempo el sentido de comunidad.
                      </>,
                      <>
                        Étape 3&nbsp;: Offrir un contrôle individuel tout en
                        préservant le sentiment d'appartenance à la communauté.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          3단계:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공동체
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          의식을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유지하면서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개인적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통제권을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제공하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>{' '}
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        The most successful workplace music strategies balance
                        collective experience with individual autonomy.
                      </>,
                      <>
                        Las estrategias musicales más exitosas en el entorno
                        laboral logran un equilibrio entre la experiencia
                        colectiva y la autonomía individual.
                      </>,
                      <>
                        Les stratégies musicales les plus efficaces en milieu de
                        travail concilient l'expérience collective et
                        l'autonomie individuelle.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          가장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          성공적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집단적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경험과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개인적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          자율성
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사이의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          균형을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞추는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>{' '}
                  <section id={'step-3-implement-headphone-policies'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Implement headphone-friendly policies with clear
                            guidelines:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Implemente políticas que permitan el uso de
                            auriculares con directrices claras:
                          </span>
                        </>,
                        <>
                          Mettez en place des politiques adaptées à
                          l'utilisation des écouteurs, avec des directives
                          claires&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            헤드폰
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            명확한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지침을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            포함한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정책을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시행하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            When and where personal headphones are acceptable
                            (probably most places, most times).
                          </>,
                          <>
                            Cuándo y dónde son aceptables los auriculares
                            personales (probablemente en la mayoría de los
                            lugares, la mayoría de las veces).
                          </>,
                          <>
                            Quand et où l'utilisation d'écouteurs personnels
                            est-elle acceptable&nbsp;? (Probablement dans la
                            plupart des endroits, la plupart du temps).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              개인용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              헤드폰을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시기와
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장소
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (아마도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대부분의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장소에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대부분의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              것입니다).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            When headphones should come off (team meetings,
                            collaborative work, emergencies).
                          </>,
                          <>
                            Cuándo hay que quitarse los auriculares (reuniones
                            de equipo, trabajo colaborativo, emergencias).
                          </>,
                          <>
                            Quand il faut retirer ses écouteurs (réunions
                            d'équipe, travail collaboratif, situations
                            d'urgence).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              헤드폰을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              벗어야
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              때
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (팀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회의,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              협업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작업,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              긴급
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              상황).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            How to signal availability despite wearing
                            headphones (special desk flags, Slack status,
                            color-coded systems).
                          </>,
                          <>
                            Cómo indicar disponibilidad a pesar de llevar
                            auriculares (banderas especiales para el escritorio,
                            estado en Slack, sistemas codificados por colores).
                          </>,
                          <>
                            Comment signaler sa disponibilité malgré le port
                            d'un casque audio&nbsp;? (Drapeaux de bureau
                            spéciaux, statut Slack, systèmes de code couleur).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              헤드폰을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              착용하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              때에도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              여부를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              알리는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방법
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (특수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              책상
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              깃발,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Slack
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              상태
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표시,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              색상
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              코드
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시스템
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              등).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'step-3-create-democratic-playlists'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Create democratic playlist contribution systems:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Crear sistemas democráticos de contribución a listas
                            de reproducción:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Créer des systèmes de contribution démocratiques
                            pour les listes de lecture&nbsp;:
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            민주적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            플레이리스트
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            참여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시스템을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구축하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Enable employees to suggest songs for office
                            playlists, building ownership and ensuring diverse
                            representation.
                          </>,
                          <>
                            Permitir que los empleados sugieran canciones para
                            las listas de reproducción de la oficina, fomentando
                            así el sentido de pertenencia y garantizando una
                            representación diversa.
                          </>,
                          <>
                            Permettre aux employés de suggérer des chansons pour
                            les listes de lecture du bureau, favorisant ainsi
                            l'appropriation et garantissant une représentation
                            diversifiée.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              직원들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사무실
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플레이리스트에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              넣을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              노래를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제안할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있도록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소속감을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              높이고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다양한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              취향이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              반영되도록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Establish clear guidelines: no explicit lyrics,
                            appropriate themes, minimum/maximum tempo ranges for
                            specific zones.
                          </>,
                          <>
                            Establecer pautas claras: no se permiten letras
                            explícitas, temas apropiados, rangos de tempo
                            mínimos y máximos para zonas específicas.
                          </>,
                          <>
                            Établir des directives claires&nbsp;: pas de paroles
                            explicites, des thèmes appropriés, des plages de
                            tempo minimales et maximales pour des zones
                            spécifiques.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              명확한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지침을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설정하세요:
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              노골적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가사
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              금지,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              적절한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주제
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선정,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              특정
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              최소/최대
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              템포
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              범위
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설정.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Rotate “DJ of the week” responsibilities, giving
                            different team members the chance to shape the sonic
                            environment.
                          </>,
                          <>
                            Roten las responsabilidades de "DJ de la semana",
                            dando a los diferentes miembros del equipo la
                            oportunidad de influir en el ambiente musical.
                          </>,
                          <>
                            Faites tourner les responsabilités de «&nbsp;DJ de
                            la semaine&nbsp;», afin de donner à différents
                            membres de l'équipe la possibilité de façonner
                            l'ambiance sonore.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              "이번
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              DJ"
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              역할을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              돌아가면서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              맡도록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              하여,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀원들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              돌아가며
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분위기를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조성할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기회를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제공하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'step-3-granular-volume-adjustments'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Provide granular volume adjustment options:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Proporcione opciones de ajuste de volumen
                            detalladas:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Proposez des options de réglage du volume
                            précises&nbsp;:
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            세밀한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            볼륨
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조절
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            옵션을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제공합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Install zone-specific volume controls so employees
                            can fine-tune without affecting other areas.
                          </>,
                          <>
                            Instale controles de volumen específicos para cada
                            zona para que los empleados puedan ajustar el sonido
                            sin afectar a otras áreas.
                          </>,
                          <>
                            Installez des commandes de volume spécifiques à
                            chaque zone afin que les employés puissent régler le
                            son avec précision sans affecter les autres zones.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              직원들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다른
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              영향을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              않고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              원하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조절할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있도록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역별
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조절
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장치를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설치하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Use decibel meters to establish maximum thresholds,
                            preventing sound from becoming oppressive.
                          </>,
                          <>
                            Utilice medidores de decibelios para establecer
                            umbrales máximos, evitando que el sonido se vuelva
                            molesto.
                          </>,
                          <>
                            Utilisez des sonomètres pour établir des seuils
                            maximaux, afin d'éviter que le bruit ne devienne
                            insupportable.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              소음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              측정기를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              최대
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              한계를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설정하고,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지나치게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              심해지는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              것을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방지하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Empower team leads to adjust volume based on
                            real-time feedback from their groups.
                          </>,
                          <>
                            Permita que los líderes de equipo ajusten el volumen
                            en función de la retroalimentación en tiempo real de
                            sus grupos.
                          </>,
                          <>
                            Permettez aux chefs d'équipe d'ajuster le volume de
                            travail en fonction des commentaires en temps réel
                            de leurs équipes.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              팀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              리더들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              그룹
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구성원들의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              실시간
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              바탕으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무량을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조정할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있도록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              권한을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부여하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'step-3-always-maintain-quiet-zones'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Always maintain true quiet zones:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Mantenga siempre zonas de silencio absoluto:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Maintenez toujours des zones de calme absolu&nbsp;:
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            항상
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            진정한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정숙
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구역을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지하십시오.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Not everyone works better with music. Respect this
                            reality by ensuring absolute quiet spaces are always
                            available.
                          </>,
                          <>
                            No todo el mundo trabaja mejor con música. Respete
                            esta realidad asegurándose de que siempre haya
                            espacios de absoluto silencio disponibles.
                          </>,
                          <>
                            Tout le monde ne travaille pas mieux en musique.
                            Respectez cette réalité en veillant à ce que des
                            espaces de calme absolu soient toujours disponibles.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              모든
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사람이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              들으면서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              더
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              효율적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              것은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아닙니다.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              이러한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              현실을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              존중하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              언제든
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              완전히
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조용한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              이용할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있도록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              보장해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            These might be conference rooms, phone booths, or
                            designated desks in corners away from speakers.
                          </>,
                          <>
                            Estos espacios pueden ser salas de conferencias,
                            cabinas telefónicas o escritorios designados en
                            rincones alejados de los altavoces.
                          </>,
                          <>
                            Il peut s'agir de salles de conférence, de cabines
                            téléphoniques ou de bureaux aménagés dans des coins,
                            à l'écart des haut-parleurs.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              이러한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간에는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회의실,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전화
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부스
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              스피커에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              떨어진
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구석에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              마련된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지정된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              책상
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              등이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              포함될
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Make quiet zones equal in quality to other
                            spaces&nbsp;— not cramped afterthoughts but
                            legitimate, comfortable work areas.
                          </>,
                          <>
                            Asegúrese de que las zonas de silencio tengan la
                            misma calidad que los demás espacios: que no sean
                            rincones estrechos e improvisados, sino áreas de
                            trabajo legítimas y cómodas.
                          </>,
                          <>
                            Faites en sorte que les zones calmes soient d'une
                            qualité égale à celle des autres espaces&nbsp;–
                            qu'il ne s'agisse pas d'espaces exigus aménagés à la
                            hâte, mais de véritables espaces de travail
                            confortables.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              조용한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다른
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              동등한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              품질로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만들어야
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              합니다.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              비좁고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대충
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만들어진
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아니라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제대로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              갖춰진
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              편안한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간이어야
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'step-4-measure-results-and-optimize'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Step 4: Measure Impact, Iterate Based on Data, and
                        Optimize Continuously
                      </>,
                      <>
                        Paso 4: Medir el impacto, iterar basándose en los datos
                        y optimizar continuamente.
                      </>,
                      <>
                        Étape 4&nbsp;: Mesurer l'impact, itérer en fonction des
                        données et optimiser en continu.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          4단계:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          영향력을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          측정하고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          데이터를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기반으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개선하며,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지속적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최적화합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Implementation isn’t a one-time project. It’s an ongoing
                        optimization process requiring measurement and
                        adjustment.
                      </>,
                      <>
                        La implementación no es un proyecto puntual. Es un
                        proceso de optimización continuo que requiere medición y
                        ajustes constantes.
                      </>,
                      <>
                        La mise en œuvre n'est pas un projet ponctuel. Il s'agit
                        d'un processus d'optimisation continu qui nécessite des
                        mesures et des ajustements.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          구현은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일회성
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로젝트가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아닙니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          데이터에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기반하여
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          측정하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조정하며
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지속적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최적화하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          과정이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지속적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최적화
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로세스입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <section id={'step-4-track-these-metrics'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>Track these metrics over 3–6 months:</>,
                        <>
                          Realice un seguimiento de estas métricas durante 3 a 6
                          meses:
                        </>,
                        <>
                          Suivez ces indicateurs sur une période de 3 à 6
                          mois&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            다음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지표들을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            3~6개월
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            동안
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추적하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Employee retention rates:
                          </span>{' '}
                          Did turnover decrease after music implementation?
                          Compare year-over-year data for the same period.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Tasas de retención de empleados:
                          </span>
                          &nbsp;¿Disminuyó la rotación de personal después de la
                          implementación de música? Compare los datos
                          interanuales para el mismo período.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Taux de fidélisation des employés&nbsp;:
                          </span>{' '}
                          Le taux de rotation du personnel a-t-il diminué après
                          la mise en place de la musique&nbsp;? Comparez les
                          données d'une année sur l'autre pour la même période.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            직원
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지율:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            도입
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            후
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이직률이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            감소했습니까?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            같은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            동안의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전년
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대비
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            데이터를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            비교해
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Productivity metrics:
                          </span>
                          &nbsp;Are tasks completing faster? Are error rates
                          decreasing? Track objective output measures, not
                          subjective feelings.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Métricas de productividad:
                          </span>
                          &nbsp; ¿Se completan las tareas más rápido? ¿Están
                          disminuyendo las tasas de error? Realice un
                          seguimiento de las medidas de rendimiento objetivas,
                          no de las percepciones subjetivas.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Indicateurs de productivité&nbsp;:
                          </span>{' '}
                          Les tâches sont-elles accomplies plus rapidement ? Les
                          taux d'erreur diminuent-ils&nbsp;? Suivez des mesures
                          de performance objectives, et non des impressions
                          subjectives.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            생산성
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지표:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            작업이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            더
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            빠르게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            완료되고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니까?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            오류
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            발생률이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            감소하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니까?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            주관적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            느낌이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            아닌
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            객관적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            결과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지표를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추적하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Stress assessments:
                          </span>{' '}
                          Conduct monthly pulse surveys asking employees to rate
                          workplace stress on a 1–10 scale. Track trends over
                          time.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Evaluaciones del estrés:
                          </span>{' '}
                          Realice encuestas mensuales para que los empleados
                          califiquen el estrés laboral en una escala del 1 al
                          10. Realice un seguimiento de las tendencias a lo
                          largo del tiempo.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Évaluations du stress&nbsp;:
                          </span>{' '}
                          Réalisez des enquêtes mensuelles auprès des employés
                          en leur demandant d'évaluer le stress au travail sur
                          une échelle de 1 à 10. Suivez l'évolution des
                          tendances au fil du temps.
                        </>,
                        <>
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스트레스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            평가:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            매달
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직원들에게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직장
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스트레스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수준을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            1점에서
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            10점
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            척도로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            평가하도록
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설문조사를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            실시하고,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경과에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            따른
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추세를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추적합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            Self-reported focus levels:
                          </span>{' '}
                          Ask “How well could you focus today?” at the end of
                          each day via a simple 1–5 scale. Average these weekly
                          and watch for patterns.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            Niveles de concentración autoinformados:
                          </span>{' '}
                          Pregunte "¿Qué tan bien pudiste concentrarte hoy?" al
                          final de cada día mediante una sencilla escala del 1
                          al 5. Calcule el promedio semanal y observe los
                          patrones.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            Niveaux de concentration autodéclarés :
                          </span>{' '}
                          À la fin de chaque journée, posez la question «&nbsp;À
                          quel point avez-vous réussi à vous concentrer
                          aujourd'hui&nbsp;?&nbsp;» à l'aide d'une simple
                          échelle de 1 à 5. Calculez la moyenne hebdomadaire et
                          observez les tendances.
                        </>,
                        <>
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보고식
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중력
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수준:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            매일
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하루를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            마칠
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            간단한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            1~5점
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            척도를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            "오늘
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            얼마나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            집중할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있었습니까?"라고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            질문하세요.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            매주
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            평균
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            내고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            패턴을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관찰하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Absenteeism rates:
                          </span>{' '}
                          Are stress-related absences declining? Check sick day
                          usage, particularly mental health days if tracked
                          separately.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Tasas de absentismo:
                          </span>
                          &nbsp;¿Están disminuyendo las ausencias relacionadas
                          con el estrés? Revise el uso de los días por
                          enfermedad, en particular los días por salud mental,
                          si se registran por separado.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Taux d'absentéisme&nbsp;:
                          </span>{' '}
                          Les absences liées au stress sont-elles en
                          baisse&nbsp;? Vérifiez le nombre de jours de congé
                          maladie, en particulier les jours pris pour des
                          raisons de santé mentale, s'ils sont comptabilisés
                          séparément.
                        </>,
                        <>
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            결근율:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스트레스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관련
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            결근이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            감소하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니까?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            병가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            현황,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특히
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정신
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            건강
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관련
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            병가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            현황(별도로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추적하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경우)을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            확인하십시오.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            Voluntary feedback volume:
                          </span>{' '}
                          Are employees proactively sharing positive or negative
                          music experiences? The quantity and tone of
                          unsolicited feedback reveals program impact.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            Volumen de comentarios voluntarios:
                          </span>{' '}
                          ¿Los empleados comparten de forma proactiva
                          experiencias musicales positivas o negativas? La
                          cantidad y el tono de los comentarios espontáneos
                          revelan el impacto del programa.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            Volume de retours spontanés&nbsp;:
                          </span>{' '}
                          Les employés partagent-ils spontanément leurs
                          expériences musicales, qu'elles soient positives ou
                          négatives&nbsp;? La quantité et le ton des
                          commentaires non sollicités.
                        </>,
                        <>
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자발적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            양:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직원들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            긍정적이든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            부정적이든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경험에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            의견을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적극적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공유하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니까?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자발적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제공되는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            양과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어조는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            프로그램의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            효과를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보여줍니다.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            className={classes.asianTextBlock}
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            확인하십시오.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Absenteeism rates:
                          </span>{' '}
                          Are stress-related absences declining? Check sick day
                          usage, particularly mental health days if tracked
                          separately.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Tasas de absentismo:
                          </span>{' '}
                          ¿Están disminuyendo las ausencias relacionadas con el
                          estrés? Revise el uso de los días por enfermedad, en
                          particular los días por salud mental, si se registran
                          por separado.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Taux d'absentéisme&nbsp;:
                          </span>{' '}
                          Les absences liées au stress sont-elles en
                          baisse&nbsp;? Vérifiez le nombre de jours de congé
                          maladie, en particulier les jours pris pour des
                          raisons de santé mentale, s'ils sont comptabilisés
                          séparément.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            결근율:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스트레스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관련
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            결근이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            감소하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니까?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            병가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            현황,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특히
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정신
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            건강
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관련
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            병가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            현황(별도로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추적하는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경우)을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            확인하십시오.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Voluntary feedback volume:
                          </span>{' '}
                          Are employees proactively sharing positive or negative
                          music experiences? The quantity and tone of
                          unsolicited feedback reveals program impact.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Volumen de comentarios voluntarios:
                          </span>{' '}
                          ¿Los empleados comparten de forma proactiva sus
                          experiencias musicales, tanto positivas como
                          negativas? La cantidad y el tono de los comentarios
                          espontáneos revelan el impacto del programa.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Volume de commentaires spontanés&nbsp;:
                          </span>{' '}
                          Les employés partagent-ils spontanément leurs
                          expériences musicales, qu'elles soient positives ou
                          négatives&nbsp;? La quantité et le ton des
                          commentaires non sollicités révèlent l'impact du
                          programme.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            자발적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            양:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직원들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            긍정적이든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            부정적이든
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경험에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            의견을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적극적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공유하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니까?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자발적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제공되는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            양과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            어조는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            프로그램의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            효과를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보여줍니다.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'step-4-adjust-based-on-patterns'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>
                          Adjust based on patterns, not individual complaints:
                        </>,
                        <>
                          Realice los ajustes basándose en patrones, no en
                          quejas individuales:
                        </>,
                        <>
                          Adaptez-vous en fonction des tendances observées, et
                          non des plaintes individuelles&nbsp;:
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            개별적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            불만이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            아닌
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            패턴을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기반으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조정하십시오.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          One person hating a particular genre doesn’t require
                          immediate change. Ten people expressing the same
                          concern does.
                        </>,
                        <>
                          Que una sola persona odie un género en particular no
                          exige un cambio inmediato. Que diez personas expresen
                          la misma preocupación sí lo exige.
                        </>,
                        <>
                          Le fait qu'une seule personne déteste un genre
                          particulier ne justifie pas un changement immédiat. En
                          revanche, si dix personnes expriment la même
                          préoccupation, cela le justifie.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사람이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특정
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장르를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            싫어한다고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            해서
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            즉시
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            변화가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            필요한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            것은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            아닙니다.
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하지만
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            열
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사람이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            같은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            우려를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            표명한다면
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이야기가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            달라집니다.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Look for patterns:
                          </span>{' '}
                          Does productivity dip on days when certain playlists
                          play? Do certain zones consistently have too few or
                          too many occupants?
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Busque patrones:
                          </span>{' '}
                          ¿Disminuye la productividad los días en que se
                          reproducen ciertas listas de reproducción? ¿Hay zonas
                          que consistentemente tienen demasiados o muy pocos
                          ocupantes?
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Recherchez des tendances&nbsp;:
                          </span>{' '}
                          La productivité diminue-t-elle les jours où certaines
                          listes de lecture sont diffusées&nbsp;? Certaines
                          zones comptent-elles systématiquement trop peu ou trop
                          de personnes&nbsp;?
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            패턴을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            찾아보세요:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특정
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재생
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            목록이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재생되는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            날에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            생산성이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            떨어지나요?
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            특정
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구역에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            항상
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            너무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적거나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            너무
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            많은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사람들이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있나요?
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanItalic}>
                            Run A/B tests:
                          </span>{' '}
                          Try different approaches in different zones or on
                          alternating weeks, measuring the impact.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Realiza pruebas A/B:
                          </span>{' '}
                          Prueba diferentes enfoques en distintas zonas o en
                          semanas alternas, midiendo el impacto.
                        </>,
                        <>
                          <span className={classes.spanItalic}>
                            Réalisez des tests A/B&nbsp;:
                          </span>{' '}
                          Essayez différentes approches dans différentes zones
                          ou une semaine sur deux, en mesurant l'impact.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            A/B
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            테스트를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            실행하세요:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            서로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            다른
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지역이나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            격주로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            다른
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            접근
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            방식을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시도하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            그
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            영향을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            측정하세요.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                  <section id={'step-4-schedule-reviews'}>
                    <h5 className={classes.subSection}>
                      {translate(
                        <>Schedule quarterly reviews:</>,
                        <>Programe revisiones trimestrales:</>,
                        <>Planifiez des examens trimestriels&nbsp;:</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            분기별
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            검토
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            일정을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            잡으세요.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Gather decision-makers and employee representatives to
                          review metrics, discuss feedback, and plan
                          adjustments.
                        </>,
                        <>
                          Reúna a los responsables de la toma de decisiones y a
                          los representantes de los empleados para revisar las
                          métricas, debatir los comentarios y planificar los
                          ajustes.
                        </>,
                        <>
                          Réunissez les décideurs et les représentants du
                          personnel pour examiner les indicateurs, discuter des
                          retours d'information et planifier les ajustements
                          nécessaires.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            의사
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            결정권자와
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직원
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            대표들을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소집하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지표를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            검토하고,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            논의하며,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개선
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            방안을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            계획하십시오.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>Update playlists seasonally to maintain freshness.</>,
                        <>
                          Actualiza las listas de reproducción según la
                          temporada para mantenerlas siempre interesantes.
                        </>,
                        <>
                          Mettez à jour vos playlists au fil des saisons pour
                          leur conserver leur fraîcheur.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            플레이리스트를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            계절별로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            업데이트하여
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            신선함을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지하세요.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                    <p className={classes.pParagraph}>
                      {translate(
                        <>
                          Reassess whether zones are serving their intended
                          purposes or need reconfiguration.
                        </>,
                        <>
                          Reevaluar si las zonas están cumpliendo sus propósitos
                          previstos o si necesitan ser reconfiguradas.
                        </>,
                        <>
                          Réévaluer si les zones remplissent les objectifs qui
                          leur ont été assignés ou si elles nécessitent une
                          reconfiguration.
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            각
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구역이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            원래
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            의도된
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            목적을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제대로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수행하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            또는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재구성할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            필요가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재평가하십시오.
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </p>
                  </section>
                </section>
              </section>
              <hr className={classes.threeDotDivisor} />
              <section id={'seven-common-mistakes-in-work-music'}>
                <h3 className={classes.subSection}>
                  {translate(
                    <>
                      7 Common Mistakes That Sabotage Workplace Music Programs
                    </>,
                    <>
                      7 errores comunes que sabotean los programas de música en
                      el lugar de trabajo
                    </>,
                    <>
                      7 erreurs courantes qui sabotent les programmes musicaux
                      en milieu de travail.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        내
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        프로그램을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        망치는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        7가지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        흔한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실수
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Learn from others’ failures. These mistakes kill
                      productivity in soundscapes programs before they can
                      deliver results.
                    </>,
                    <>
                      Aprende de los fracasos de los demás. Estos errores acaban
                      con la productividad en los programas de diseño de
                      paisajes sonoros antes de que puedan ofrecer resultados.
                    </>,
                    <>
                      Tirez des leçons des échecs des autres. Ces erreurs
                      nuisent à la productivité des programmes d'aménagement
                      sonore avant même qu'ils ne puissent produire des
                      résultats.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다른
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사람들의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실패
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사례에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        교훈을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        얻으세요.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이러한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실수들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        환경
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        개선
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        프로그램이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        성과를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        내기도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        생산성을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저해합니다.
                      </span>{' '}
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'mistake-1-just-one-playlist'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>Mistake #1: One Playlist for the Entire Office</>,
                      <>
                        Error 1: Una sola lista de reproducción para toda la
                        oficina.
                      </>,
                      <>
                        Erreur&nbsp;1&nbsp;: Une seule liste de lecture pour
                        tout le bureau.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          실수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          #1:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사무실
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전체에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하나의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트만
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>The problem:</span>{' '}
                        Analytical work needs 60–70 BPM instrumental music.
                        Physical tasks benefit from 140+ BPM energetic tracks.
                        Collaborative work thrives with 100–130 BPM upbeat
                        sounds. A single playlist compromises everyone’s
                        productivity.
                      </>,
                      <>
                        <span className={classes.spanItalic}>El problema:</span>{' '}
                        El trabajo analítico requiere música instrumental de 60
                        a 70 pulsaciones por minuto (BPM). Las tareas físicas se
                        benefician de pistas enérgicas de más de 140 BPM. El
                        trabajo colaborativo prospera con sonidos animados de
                        100 a 130 BPM. Una sola lista de reproducción compromete
                        la productividad de todos.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          Le problème&nbsp;:
                        </span>{' '}
                        Les tâches analytiques nécessitent une musique
                        instrumentale à 60-70 BPM. Les tâches physiques sont
                        facilitées par des morceaux énergiques à plus de 140
                        BPM. Le travail collaboratif est optimisé par des
                        musiques entraînantes à 100-130 BPM. Une seule et unique
                        liste de lecture nuit à la productivité de chacun.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제점:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분석적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업에는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분당
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          60~70비트(BPM)의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          연주곡이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요하고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          신체
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활동에는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          140BPM
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이상의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활기찬
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과적입니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          협업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업에는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          100~130BPM의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경쾌한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          적합합니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하나의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트로는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사람의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생산성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          만족시킬
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          없습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The consequence:
                        </span>{' '}
                        Employees either tune out the music (wasting the
                        investment) or find it actively distracting (harming
                        productivity).
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La consecuencia:
                        </span>{' '}
                        Los empleados o bien ignoran la música (desperdiciando
                        la inversión) o la encuentran activamente molesta
                        (perjudicando la productividad).
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La conséquence&nbsp;:
                        </span>{' '}
                        Les employés ignorent la musique (ce qui rend
                        l'investissement inutile) ou la trouvent carrément
                        gênante (ce qui nuit à la productivité).
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          무시하거나(투자
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          낭비)
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오히려
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          때문에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중력이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          떨어져
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생산성에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          해를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          입게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          됩니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The solution:
                        </span>{' '}
                        Implement zone-based strategies with distinct
                        soundscapes for focus areas, collaborative spaces, and
                        break rooms. Match music characteristics to cognitive
                        demands.
                      </>,
                      <>
                        <span className={classes.spanItalic}>La solución:</span>{' '}
                        Implementar estrategias basadas en zonas con paisajes
                        sonoros distintos para áreas de concentración, espacios
                        de colaboración y salas de descanso. Adaptar las
                        características de la música a las exigencias
                        cognitivas.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La solution&nbsp;:
                        </span>{' '}
                        Mettre en œuvre des stratégies par zones avec des
                        ambiances sonores distinctes pour les espaces de
                        concentration, les espaces de collaboration et les
                        salles de pause. Adapter les caractéristiques musicales
                        aux exigences cognitives.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          협업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          휴게실
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          등
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          각
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역별로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차별화된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음향
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조성하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기반
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구현하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          특성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          각
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인지적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요구
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사항에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞춰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조정하십시오.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'mistake-2-volume-too-loud'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>Mistake #2: Volume Too Loud (Above 75&nbsp;Decibels)</>,
                      <>
                        Error 2: Volumen demasiado alto (por encima de 75
                        decibelios)
                      </>,
                      <>
                        Erreur&nbsp;2&nbsp;: Volume trop élevé (au-dessus de 75
                        décibels)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          실수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          #2:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소리가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          너무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          크다
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (75데시벨
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이상)
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>The problem:</span>{' '}
                        Once music exceeds 75 decibels, it shifts from helpful
                        background to distracting foreground noise. Employees
                        strain to hear phone calls. The music itself becomes a
                        stressor rather than stress relief.
                      </>,
                      <>
                        <span className={classes.spanItalic}>El problema:</span>{' '}
                        Cuando la música supera los 75 decibelios, deja de ser
                        un sonido de fondo útil para convertirse en un ruido de
                        primer plano que distrae. Los empleados se esfuerzan por
                        escuchar las llamadas telefónicas. La música se
                        convierte en una fuente de estrés en lugar de un alivio
                        para el estrés.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          Le problème&nbsp;:
                        </span>{' '}
                        Dès que la musique dépasse 75 décibels, elle passe d'un
                        bruit de fond utile à un bruit de fond gênant. Les
                        employés ont du mal à entendre les conversations
                        téléphoniques. La musique devient alors une source de
                        stress plutôt qu'un moyen de se détendre.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소리가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          75데시벨을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          넘어서면
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          도움이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          되는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          배경음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아니라
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          방해하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          된다는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것입니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전화
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통화를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제대로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          듣기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          애쓰게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          되고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          자체가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스트레스
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          해소제가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아닌
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스트레스
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유발
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요인이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          됩니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The consequence:
                        </span>{' '}
                        Physical fatigue from elevated heart rate and blood
                        pressure. Decreased rather than increased focus.
                        Employee complaints and pushback against the music
                        program.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La consecuencia:
                        </span>{' '}
                        Fatiga física debido al aumento de la frecuencia
                        cardíaca y la presión arterial. Disminución de la
                        concentración en lugar de un aumento. Quejas de los
                        empleados y rechazo al programa musical.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La conséquence&nbsp;:
                        </span>{' '}
                        Fatigue physique due à une augmentation du rythme
                        cardiaque et de la tension artérielle. Diminution de la
                        concentration au lieu d'une amélioration. Plaintes des
                        employés et opposition au programme musical.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          심박수와
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          혈압
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          상승으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          신체적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          피로.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중력
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          향상이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아닌
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오히려
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저하.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          불만과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로그램에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          반발.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The solution:
                        </span>{' '}
                        Maintain office background playlists at 60–70 decibels
                        maximum. Purchase an inexpensive decibel meter or use a
                        smartphone app to verify actual levels. What feels
                        “moderate” to managers often measures much louder than
                        realized.
                      </>,
                      <>
                        <span className={classes.spanItalic}>La solución:</span>{' '}
                        Mantener las listas de reproducción de música ambiental
                        en la oficina a un máximo de 60-70 decibelios. Adquiera
                        un sonómetro económico o utilice una aplicación para
                        smartphone para verificar los niveles reales. Lo que
                        para los gerentes parece un volumen "moderado" suele ser
                        mucho más alto de lo que creen.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La solution&nbsp;:
                        </span>{' '}
                        Maintenez le volume des listes de lecture diffusées en
                        fond sonore dans les bureaux à un maximum de 60 à 70
                        décibels. Achetez un sonomètre peu coûteux ou utilisez
                        une application pour smartphone afin de vérifier les
                        niveaux sonores actuels. Ce qui semble
                        «&nbsp;modéré&nbsp;» aux responsables est souvent
                        beaucoup plus bruyant qu'ils ne le pensent.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사무실
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          배경
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          볼륨을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최대
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          60~70데시벨로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유지하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저렴한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          측정기를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구입하거나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스마트폰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          앱을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하여
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실제
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수준을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          확인하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          관리자에게는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          "적당하다"고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          느껴지는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음도
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실제로는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생각보다
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          훨씬
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시끄러운
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경우가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          많습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'mistake-3-ignore-employee-input'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>Mistake #3: Completely Ignoring Employee Input</>,
                      <>
                        Error 3: Ignorar por completo las opiniones de los
                        empleados.
                      </>,
                      <>
                        Erreur&nbsp;3&nbsp;: Ignorer complètement l'avis des
                        employés.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          세
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          번째
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실수:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          의견을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          완전히
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          무시하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>The problem:</span>{' '}
                        Management selects music based on their own preferences
                        or generic “focus music” recommendations without
                        consulting the people actually working in the
                        environment daily.
                      </>,
                      <>
                        <span className={classes.spanItalic}>El problema:</span>{' '}
                        La dirección selecciona la música basándose en sus
                        propias preferencias o en recomendaciones genéricas de
                        "música para concentrarse" sin consultar a las personas
                        que trabajan a diario en ese entorno.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          Le problème&nbsp;:
                        </span>{' '}
                        La direction choisit la musique en fonction de ses
                        propres préférences ou de recommandations génériques de
                        «&nbsp;musique de concentration&nbsp;» sans consulter
                        les personnes qui travaillent quotidiennement dans cet
                        environnement.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제점:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경영진은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매일
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실제로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사람들의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          의견을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          묻지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          않고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          자신들의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          취향이나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일반적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          "집중력
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          향상
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악"
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          추천에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          따라
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선택합니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The consequence:
                        </span>{' '}
                        Resentment, low adoption, employees using personal
                        headphones to escape the imposed soundscape, and failure
                        to achieve the morale benefits music should provide.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La consecuencia:
                        </span>{' '}
                        Resentimiento, baja aceptación, empleados que usan
                        auriculares personales para evadir el ambiente sonoro
                        impuesto y la imposibilidad de obtener los beneficios
                        para la moral que la música debería proporcionar.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La conséquence&nbsp;:
                        </span>{' '}
                        Ressentiment, faible adoption, employés utilisant des
                        écouteurs personnels pour échapper à l'environnement
                        sonore imposé, et incapacité à obtenir les avantages en
                        termes de moral que la musique devrait apporter.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다음과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          같습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          불만
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          증가,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          낮은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수용도,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          강제로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          듣게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          되는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          벗어나기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          헤드폰을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          현상,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그리고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제공해야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          진작
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          얻지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          못하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것입니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The solution:
                        </span>{' '}
                        Conduct surveys before implementation. Create
                        contribution systems where employees can suggest tracks.
                        Rotate “DJ” responsibilities. Treat employee input
                        seriously and implement requested changes when they make
                        sense.
                      </>,
                      <>
                        <span className={classes.spanItalic}>La solución:</span>{' '}
                        Realizar encuestas antes de la implementación. Crear
                        sistemas de participación donde los empleados puedan
                        sugerir canciones. Rotar las responsabilidades de "DJ".
                        Tomar en serio las sugerencias de los empleados e
                        implementar los cambios solicitados cuando sean
                        pertinentes.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La solution&nbsp;:
                        </span>
                        Réaliser des sondages avant la mise en œuvre. Créer des
                        systèmes de contribution permettant aux employés de
                        suggérer des morceaux. Alterner les responsabilités de
                        «&nbsp;DJ&nbsp;». Prendre au sérieux les suggestions des
                        employés et mettre en œuvre les changements demandés
                        lorsqu'ils sont pertinents.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실행
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          설문조사를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실시하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          추천할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시스템을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          만드세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          'DJ'
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          역할을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          돌아가면서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맡도록
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          의견을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          진지하게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          받아들이고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합리적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요청은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실제로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          반영하세요.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'mistake-4-lyrical-music-in-focus-zones'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>Mistake #4: Lyric-Heavy Music During Analytical Work</>,
                      <>
                        Error 4: Escuchar música con muchas letras durante el
                        trabajo analítico.
                      </>,
                      <>
                        Erreur&nbsp;4&nbsp;: écouter de la musique avec beaucoup
                        de lettres pendant le travail analítico.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          실수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          #4:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분석적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          때
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가사가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          많은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          듣는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>The problem:</span>{' '}
                        Language processing centers cannot simultaneously handle
                        song lyrics and verbal work tasks like writing, reading,
                        or learning without severe performance degradation.
                      </>,
                      <>
                        <span className={classes.spanItalic}>El problema:</span>{' '}
                        Los centros de procesamiento del lenguaje no pueden
                        gestionar simultáneamente las letras de canciones y las
                        tareas verbales como escribir, leer o aprender sin que
                        se produzca un grave deterioro del rendimiento.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          Le problème&nbsp;:
                        </span>{' '}
                        Les centres de traitement du langage ne peuvent pas
                        gérer simultanément les paroles de chansons et les
                        tâches verbales telles que l'écriture, la lecture ou
                        l'apprentissage sans une dégradation importante des
                        performances.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제점:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          언어
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          처리
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          센터는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          노래
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가사와
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          글쓰기,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          읽기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          학습과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          같은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          언어
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          관련
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          동시에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          처리할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경우
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          심각한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          성능
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저하
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          없이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수행할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          없습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The consequence:
                        </span>{' '}
                        Employees performing worse with music than they would in
                        silence, contradicting the entire purpose of the
                        program.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La consecuencia:
                        </span>{' '}
                        Los empleados rinden peor con música que en silencio, lo
                        que contradice el propósito principal del programa.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La conséquence&nbsp;:
                        </span>{' '}
                        Les employés sont moins performants avec de la musique
                        qu'ils ne le seraient en silence, ce qui contredit
                        l'objectif même du programme.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          들으면서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          때보다
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조용한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          때
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          더
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          나은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          성과를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보였으며,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로그램의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          본래
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          목적과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          완전히
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          상반되는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과였습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The solution:
                        </span>{' '}
                        Reserve lyrical music for break rooms and periods
                        dedicated to repetitive physical tasks. Use exclusively
                        instrumental tracks during focus hours in concentration
                        zones.
                      </>,
                      <>
                        <span className={classes.spanItalic}>La solución:</span>{' '}
                        Reserva la música con letra para las salas de descanso y
                        los periodos dedicados a tareas físicas repetitivas.
                        Utiliza exclusivamente música instrumental durante las
                        horas de concentración en las zonas de trabajo.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La solution&nbsp;:
                        </span>{' '}
                        Réservez la musique avec paroles aux salles de pause et
                        aux périodes consacrées aux tâches physiques
                        répétitives. Utilisez exclusivement des morceaux
                        instrumentaux pendant les heures de concentration dans
                        les zones de travail nécessitant une grande
                        concentration.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          휴게실이나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          반복적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          신체
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활동을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간에는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가사가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          틀고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중력이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간에는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오직
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기악곡만
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하세요.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'mistake-5-no-quiet-zones'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>Mistake #5: No Quiet Zones Available Anywhere</>,
                      <>
                        Error 5: No hay zonas tranquilas disponibles en ningún
                        lugar.
                      </>,
                      <>
                        Erreur&nbsp;5&nbsp;: Aucune zone de calme n'est
                        disponible.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          실수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          #5:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조용한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전혀
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          마련되어
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          않음
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>The problem:</span>{' '}
                        Some people genuinely cannot focus with any background
                        music or sound. Their brains process all audio as
                        potential information requiring attention. Forcing a
                        music-everywhere environment drives away talented
                        employees who need silence.
                      </>,
                      <>
                        <span className={classes.spanItalic}>El problema:</span>{' '}
                        Algunas personas simplemente no pueden concentrarse con
                        música o ruido de fondo. Sus cerebros procesan todo el
                        audio como información potencial que requiere atención.
                        Imponer un ambiente con música constante ahuyenta a los
                        empleados talentosos que necesitan silencio.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          Le problème&nbsp;:
                        </span>{' '}
                        Certaines personnes sont véritablement incapables de se
                        concentrer avec de la musique ou du bruit de fond. Leur
                        cerveau traite tous les sons comme des informations
                        potentielles nécessitant leur attention. Imposer un
                        environnement musical permanent fait fuir les employés
                        talentueux qui ont besoin de silence.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제점:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          어떤
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사람들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          배경
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있으면
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전혀
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          없습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그들의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          뇌는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소리를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          주의를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기울여야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          잠재적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정보로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          처리하기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          때문입니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          곳에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          흘러나오는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          강요하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조용한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유능한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          떠나게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          만들
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The consequence:
                        </span>{' '}
                        Reduced diversity of cognitive styles in your workforce.
                        High performers who need silence leave for competitors
                        offering quiet spaces.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La consecuencia:
                        </span>{' '}
                        Reducción de la diversidad de estilos cognitivos en su
                        plantilla. Los empleados de alto rendimiento que
                        necesitan silencio se marchan a la competencia, que
                        ofrece espacios de trabajo tranquilos.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La conséquence&nbsp;:
                        </span>{' '}
                        Réduction de la diversité des styles cognitifs dans
                        votre plantille. Les employés de haut niveau rendent ce
                        dont ils ont besoin le silence se marche à la
                        compétence, qui offre des espaces de travail tranquille.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          내
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스타일의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다양성이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          감소합니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조용한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          뛰어난
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인재들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조용한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제공하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경쟁사로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이직하게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          됩니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The solution:
                        </span>{' '}
                        Always designate and protect true quiet zones&nbsp;—
                        conference rooms, phone booths, corners, or entire
                        sections where silence is the rule. Make these areas
                        equal in quality and comfort to other workspaces.
                      </>,
                      <>
                        <span className={classes.spanItalic}>La solución:</span>{' '}
                        Designar y proteger siempre zonas de verdadero silencio:
                        salas de reuniones, cabinas telefónicas, rincones o
                        secciones enteras donde el silencio sea la norma.
                        Asegúrese de que estas áreas tengan la misma calidad y
                        comodidad que los demás espacios de trabajo.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La solution&nbsp;:
                        </span>{' '}
                        Aménagez et protégez des zones de calme absolu, comme
                        des salles de réunion, des cabines téléphoniques, des
                        coins isolés ou des sections entières où le silence est
                        de rigueur. Veillez à ce que ces espaces soient aussi
                        confortables et agréables que les autres espaces de
                        travail.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          회의실,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전화
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부스,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구석진
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전체
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구역
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          등
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조용함이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유지되어야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          진정한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조용한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지정하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보호하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다른
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          공간과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          마찬가지로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          품질과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          편안함
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          면에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          동등해야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'mistake-6-using-consumer-music-services'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Mistake #6: Using Consumer Streaming Services Illegally
                      </>,
                      <>
                        Error 6: Utilizar servicios de streaming para
                        consumidores de forma ilegal.
                      </>,
                      <>
                        Erreur&nbsp;6&nbsp;: Utiliser illégalement des services
                        de streaming destinés au grand public.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          여섯
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          번째
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실수:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소비자용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스트리밍
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서비스를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          불법적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이용하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>The problem:</span>{' '}
                        Spotify Premium, Apple Music, YouTube, and other
                        consumer platforms are licensed exclusively for
                        personal, non-commercial use. Playing them in business
                        environments violates copyright law, exposing companies
                        to legal risk and depriving artists of fair
                        compensation.
                      </>,
                      <>
                        <span className={classes.spanItalic}>El problema:</span>{' '}
                        Spotify Premium, Apple Music, YouTube y otras
                        plataformas de consumo están licenciadas exclusivamente
                        para uso personal y no comercial. Reproducirlas en
                        entornos empresariales infringe la ley de derechos de
                        autor, exponiendo a las empresas a riesgos legales y
                        privando a los artistas de una compensación justa.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          Le problème&nbsp;:
                        </span>{' '}
                        Spotify Premium, Apple Music, YouTube et d'autres
                        plateformes grand public sont concédées sous licence
                        exclusivement pour un usage personnel et non commercial.
                        Les utiliser dans un cadre professionnel constitue une
                        violation du droit d'auteur, exposant les entreprises à
                        des risques juridiques et privant les artistes d'une
                        rémunération équitable.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제점:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스포티파이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프리미엄,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          애플
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          뮤직,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유튜브
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기타
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소비자용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플랫폼은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개인적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비상업적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          용도로만
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하도록
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부여되어
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플랫폼을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사업장에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저작권법을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위반하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          행위이며,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기업은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          법적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위험에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          노출되고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아티스트는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정당한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보상을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          받지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          못하게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          됩니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The consequence:
                        </span>{' '}
                        Potential lawsuits, fines, and licensing violations.
                        Reputational damage if the media covers your company’s
                        copyright infringement.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La consecuencia:
                        </span>{' '}
                        Posibles demandas, multas e infracciones de licencias.
                        Daño a la reputación si los medios de comunicación
                        cubren la infracción de derechos de autor de su empresa.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La conséquence&nbsp;:
                        </span>{' '}
                        Poursuites judiciaires potentielles, amendes et
                        violations des conditions de licence. Atteinte à la
                        réputation si les médias couvrent la violation des
                        droits d'auteur commise par votre entreprise.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소송,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          벌금,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위반
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          등의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          문제가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          발생할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          언론에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          귀사의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저작권
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          침해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사실을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보도할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경우
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이미지에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          심각한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          손상을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          입을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The solution:
                        </span>{' '}
                        Invest in commercial music services specifically
                        licensed for business use: Soundsuit, Cloud Cover Music,
                        Rockbot, Soundtrack Your Brand, Practical Stream, or
                        similar platforms. These services cost more but provide
                        legal protection and ensure artists are compensated. The
                        cost is a legitimate business expense easily justified
                        by productivity gains.
                      </>,
                      <>
                        <span className={classes.spanItalic}>La solución:</span>{' '}
                        Invierta en servicios de música comercial con licencia
                        específica para uso empresarial: Soundsuit, Cloud Cover
                        Music, Rockbot, Soundtrack Your Brand, Practical Stream
                        o plataformas similares. Estos servicios son más caros,
                        pero ofrecen protección legal y garantizan la
                        compensación a los artistas. El costo es un gasto
                        empresarial legítimo que se justifica fácilmente por el
                        aumento de la productividad.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La solution&nbsp;:
                        </span>{' '}
                        Investissez dans des services de musique commerciale
                        spécifiquement sous licence pour un usage
                        professionnel&nbsp;: Soundsuit, Cloud Cover Music,
                        Rockbot, Soundtrack Your Brand, Practical Stream ou des
                        plateformes similaires. Ces services coûtent plus cher,
                        mais offrent une protection juridique et garantissent la
                        rémunération des artistes. Ce coût représente une
                        dépense professionnelle légitime, facilement justifiée
                        par les gains de productivité.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다음과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          같습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사업장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          특별히
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부여된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          상업용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서비스에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          투자하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Soundsuit,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Cloud
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Cover
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Music,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Rockbot,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Soundtrack
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Your
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Brand,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Practical
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          Stream
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이와
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유사한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플랫폼을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이용할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서비스는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비용이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          더
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          들지만
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          법적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보호를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제공하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아티스트에게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정당한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보상이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지급되도록
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보장합니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이러한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비용은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생산성
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          향상으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          충분히
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정당화될
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합법적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비용입니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
                <section id={'mistake-7-no-new-songs-or-feedback'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        Mistake #7: Never Updating Playlists or Seeking Feedback
                      </>,
                      <>
                        Error 7: No actualizar nunca las listas de reproducción
                        ni solicitar comentarios.
                      </>,
                      <>
                        Erreur&nbsp;7&nbsp;: Ne jamais mettre à jour ses listes
                        de lecture ni solliciter d'avis.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          실수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          #7:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          재생
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          목록을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업데이트하거나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          피드백을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구하지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          않는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>The problem:</span>{' '}
                        The same 30–50 songs on repeat for months creates audio
                        fatigue. Employees begin actively resenting the music,
                        and what once enhanced focus becomes an irritant.
                      </>,
                      <>
                        <span className={classes.spanItalic}>El problema:</span>{' '}
                        Escuchar las mismas 30-50 canciones una y otra vez
                        durante meses provoca fatiga auditiva. Los empleados
                        empiezan a sentir un rechazo activo hacia la música, y
                        lo que antes mejoraba la concentración se convierte en
                        una molestia.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          Le problème&nbsp;:
                        </span>{' '}
                        Écouter les mêmes 30 à 50 chansons en boucle pendant des
                        mois crée une fatigue auditive. Les employés finissent
                        par détester la musique, et ce qui favorisait autrefois
                        la concentration devient une source d'irritation.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제점:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          몇
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          달
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          동안
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          똑같은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          30~50곡이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          반복해서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          재생되면
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          청각적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          피로감이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          쌓입니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          적극적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          불만을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          갖게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          되고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          한때
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          집중력
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          향상에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          도움이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          되었던
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오히려
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          짜증을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유발하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요소가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          됩니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The consequence:
                        </span>{' '}
                        Diminishing returns from the music program. Eventually,
                        the playlist becomes so tiresome that employees either
                        tune it out completely or find it actively annoying.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La consecuencia:
                        </span>{' '}
                        Rendimiento decreciente del programa musical. Con el
                        tiempo, la lista de reproducción se vuelve tan monótona
                        que los empleados la ignoran por completo o les resulta
                        francamente molesta.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La conséquence&nbsp;:
                        </span>{' '}
                        Rendement décroissant du programme musical. À terme, la
                        liste de lecture devient tellement lassante que les
                        employés finissent par l'ignorer complètement ou la
                        trouvent carrément agaçante.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결과,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로그램의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          점점
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          떨어집니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          결국
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          너무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지루해져서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아예
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          듣지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          않거나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오히려
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          짜증을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          느끼게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          됩니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanItalic}>
                          The solution:
                        </span>{' '}
                        Rotate playlists weekly or bi-weekly. Add seasonal
                        variations (summer chill vibes, winter cozy
                        instrumentals). Let different departments curate monthly
                        selections. Continuously gather feedback and implement
                        changes. Keep the sonic environment dynamic while
                        maintaining core characteristics that serve
                        productivity.
                      </>,
                      <>
                        <span className={classes.spanItalic}>La solución:</span>{' '}
                        Gire las listas de reproducción semanal o
                        quincenalmente. Añada variaciones estacionales (música
                        relajante para el verano, instrumentales acogedores para
                        el invierno). Permita que diferentes departamentos
                        seleccionen la música mensualmente. Recopile comentarios
                        continuamente e implemente cambios. Mantenga un ambiente
                        sonoro dinámico, conservando al mismo tiempo las
                        características principales que favorecen la
                        productividad.
                      </>,
                      <>
                        <span className={classes.spanItalic}>
                          La solution&nbsp;:
                        </span>{' '}
                        Faites tourner les listes de lecture chaque semaine ou
                        toutes les deux semaines. Ajoutez des variations
                        saisonnières (ambiance estivale décontractée, musiques
                        instrumentales chaleureuses pour l'hiver). Laissez
                        différents services sélectionner des morceaux chaque
                        mois. Recueillez régulièrement des commentaires et
                        apportez des modifications. Maintenez un environnement
                        sonore dynamique tout en conservant les caractéristiques
                        essentielles qui favorisent la productivité.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매주
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          격주로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          변경하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          계절별
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분위기에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞춰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (여름에는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          편안한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분위기,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          겨울에는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아늑한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          연주곡)
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다양한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          추가하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          각
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부서에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매달
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          플레이리스트를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선정하도록
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지속적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          피드백을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수집하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개선
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사항을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          적용하세요.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생산성
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          향상에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          도움이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          되는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          핵심
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요소를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유지하면서도
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          역동적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          조성하세요.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
              </section>
              <hr className={classes.threeDotDivisor} />
              <section id={'week-by-week-implementation-checklist'}>
                <h3 className={classes.subSection}>
                  {translate(
                    <>
                      Quick Start: Your Week-by-Week Implementation Checklist
                    </>,
                    <>
                      Guía de inicio rápido: Lista de verificación de
                      implementación semana a semana
                    </>,
                    <>
                      Guide de démarrage rapide&nbsp;: Votre liste de contrôle
                      de mise en œuvre semaine par semaine
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        빠른
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시작:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        주별
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실행
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        체크리스트
                      </span>{' '}
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Ready to transform your workplace with strategic sound
                      design? Here’s your actionable timeline.
                    </>,
                    <>
                      ¿Listo para transformar tu lugar de trabajo con un diseño
                      de sonido estratégico? Aquí tienes tu cronograma de
                      acciones.
                    </>,
                    <>
                      Prêt à transformer votre espace de travail grâce à une
                      conception sonore stratégique&nbsp;? Voici votre
                      calendrier d'action.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전략적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사운드
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        디자인으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        직장을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        변화시킬
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        준비가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        되셨나요?
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        여기
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실천
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가능한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        일정표가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        주간
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실행
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        체크리스트
                      </span>{' '}
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'week-1-foundation-and-assessment'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>Week 1: Foundation and Assessment</>,
                      <>Semana 1: Fundamentos y evaluación</>,
                      <>Semaine 1&nbsp;: Fondements et évaluation</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          1주차:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기초
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다지기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          평가
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <section id={'day-1-and-2-research-and-education'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 1–2: Research and Education
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 1-2: Investigación y educación
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jour 1-2&nbsp;: Recherche et formation
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            디아스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            1-2:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조사와
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            교육
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Share Part 1 of this guide with decision-makers to
                            build buy-in around the neuroscience
                          </>,
                          <>
                            Comparta la Parte 1 de esta guía con los
                            responsables de la toma de decisiones para generar
                            consenso en torno a la neurociencia.
                          </>,
                          <>
                            Partagez la première partie de ce guide avec les
                            décideurs afin d'obtenir leur adhésion aux principes
                            des neurosciences.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가이드의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              1부를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              의사결정권자들과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공유하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              신경과학에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공감대를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              형성하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Research commercial music licensing services
                            (Soundsuit, Cloud Cover Music, Practical Stream,
                            Rockbot)
                          </>,
                          <>
                            Investigue los servicios de licencias de música
                            comercial (Soundsuit, Cloud Cover Music, Practical
                            Stream, Rockbot)
                          </>,
                          <>
                            Renseignez-vous sur les services de licence musicale
                            commerciale (Soundsuit, Cloud Cover Music, Practical
                            Stream, Rockbot)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              상업용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              라이선스
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              서비스(Soundsuit,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Cloud
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Cover
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Music,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Practical
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Stream,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              Rockbot)를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조사해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              보세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Identify your implementation team (facilities, HR,
                            employee representatives)
                          </>,
                          <>
                            Identifique a su equipo de implementación
                            (instalaciones, recursos humanos, representantes de
                            los empleados).
                          </>,
                          <>
                            Identifiez votre équipe de mise en œuvre (services
                            généraux, ressources humaines, représentants du
                            personnel).
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              구현
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              팀(시설
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관리팀,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              인사팀,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              직원
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대표)을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구성하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'day-3-to-5-workplace-analysis'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 3–5: Workplace Analysis
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 3-5: Análisis del entorno laboral
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jours 3 à 5&nbsp;: Analyse du lieu de travail
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            3일차~5일차:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직장
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            환경
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분석
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Map your office layout noting acoustic zones and
                            natural boundaries
                          </>,
                          <>
                            Diseñe un plano de la distribución de su oficina,
                            indicando las zonas acústicas y los límites
                            naturales.
                          </>,
                          <>
                            Cartographiez l'aménagement de vos bureaux en
                            indiquant les zones acoustiques et les limites
                            naturelles.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              사무실
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              레이아웃을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              파악하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음향
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              자연적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경계를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표시하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Measure current noise levels with a decibel meter at
                            different times of day
                          </>,
                          <>
                            Mida los niveles de ruido actuales con un sonómetro
                            en diferentes momentos del día.
                          </>,
                          <>
                            Mesurez les niveaux sonores actuels à l'aide d'un
                            sonomètre à différents moments de la journée.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              데시벨
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              측정기를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              하루
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              여러
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시간대에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수준을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              측정하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Catalog existing audio equipment (speakers, systems,
                            potential quiet zones)
                          </>,
                          <>
                            Catalogar los equipos de audio existentes
                            (altavoces, sistemas, posibles zonas silenciosas)
                          </>,
                          <>
                            Répertorier les équipements audio existants
                            (haut-parleurs, systèmes, zones potentiellement
                            calmes)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              기존
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              오디오
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장비(스피커,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시스템,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              잠재적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              차단
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역)
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목록을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              작성합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Document employee task distribution (focus work,
                            collaborative work, routine tasks)
                          </>,
                          <>
                            Documentar la distribución de tareas de los
                            empleados (trabajo individual, trabajo colaborativo,
                            tareas rutinarias)
                          </>,
                          <>
                            Documenter la répartition des tâches des employés
                            (travail individuel, travail collaboratif, tâches de
                            routine)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              직원
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분담
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              현황을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              문서화하세요
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (집중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              협업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일상적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'day-6-and-7-employee-survey'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 6–7: Employee Survey
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 6-7: Encuesta a los empleados
                          </span>
                        </>,
                        <>Jour 6-7&nbsp;: Enquête auprès des employés</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            6일차~7일차:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            직원
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설문조사
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Create anonymous survey asking about music
                            preferences, distractions, and desired changes
                          </>,
                          <>
                            Crea una encuesta anónima que pregunte sobre
                            preferencias musicales, distracciones y cambios
                            deseados.
                          </>,
                          <>
                            Créez un sondage anonyme portant sur les préférences
                            musicales, les sources de distraction et les
                            changements souhaités.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              취향,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              방해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              요소,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              그리고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              원하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              변화에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              묻는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              익명
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설문조사를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만드세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Distribute survey to all employees with 1-week
                            deadline
                          </>,
                          <>
                            Distribuir la encuesta a todos los empleados con un
                            plazo de una semana.
                          </>,
                          <>
                            Distribuer le questionnaire à tous les employés avec
                            un délai d'une semaine.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              모든
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              직원에게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설문지를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              배포하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              답변
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제출
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기한은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              1주일로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              정하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Schedule focus groups with representatives from
                            different departments
                          </>,
                          <>
                            Programa grupos de discusión con representantes de
                            diferentes departamentos.
                          </>,
                          <>
                            Planifiez des groupes de discussion avec des
                            représentants des différents départements.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              다양한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부서의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              담당자들과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              함께
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              포커스
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              그룹
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일정을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              잡으세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'week-2-planning-and-setup'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Week 2: Planning and Setup
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Semana 2: Planificación y configuración
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Semaine 2&nbsp;: Planification et configuration
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          2주차:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          계획
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          설정
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <section id={'day-8-to-10-data-analysis-and-strategy'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 8–10: Data Analysis and Strategy Development
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 8-10: Análisis de datos y desarrollo de
                            estrategias
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jours 8 à 10&nbsp;: Analyse des données et
                            développement de la stratégie
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            8일차~10일차:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            데이터
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분석
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            및
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전략
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개발
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Analyze survey results for patterns and preferences
                          </>,
                          <>
                            Analice los resultados de la encuesta para
                            identificar patrones y preferencias.
                          </>,
                          <>
                            Analysez les résultats de l'enquête afin
                            d'identifier les tendances et les préférences.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              설문조사
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              결과를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분석하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              패턴과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선호도를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              파악하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Identify 2–4 distinct workplace zones based on work
                            types
                          </>,
                          <>
                            Identifique entre 2 y 4 zonas de trabajo distintas
                            según los tipos de tareas que se realicen en cada
                            una.
                          </>,
                          <>
                            Identifiez 2 à 4 zones de travail distinctes en
                            fonction des types de tâches effectuées.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              유형에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              2~4개의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              서로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              다른
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구분하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Select commercial music service and create account
                          </>,
                          <>
                            Seleccione un servicio de música comercial y cree
                            una cuenta.
                          </>,
                          <>
                            Sélectionnez un service de musique commerciale et
                            créez un compte.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              상업용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              서비스를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선택하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              계정을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만드세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Draft headphone policy and zone usage guidelines</>,
                          <>
                            Proyecto de política de auriculares y directrices de
                            uso por zonas
                          </>,
                          <>
                            Projet de politique relative aux casques audio et
                            directives d'utilisation des zones
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              헤드폰
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              정책
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              및
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역별
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지침
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              초안
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'day-13-and-14-technical-setup'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 13–14: Technical Setup
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 13-14: Configuración técnica
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jour 13-14&nbsp;: Configuration technique
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            13일차~14일차:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기술
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설정
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Test audio equipment in each zone</>,
                          <>Pruebe el equipo de audio en cada zona.</>,
                          <>Testez l'équipement audio dans chaque zone.</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              각
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              오디오
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장비를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              테스트하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Install additional speakers if needed for zone
                            coverage
                          </>,
                          <>
                            Instale altavoces adicionales si es necesario para
                            cubrir todas las zonas.
                          </>,
                          <>
                            Installez des haut-parleurs supplémentaires si
                            nécessaire pour couvrir toute la zone.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              필요에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              커버리지를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              추가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              스피커를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설치하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Configure volume levels (40–50 dB focus zones, 60–70
                            dB collaborative, 55–65 dB reception)
                          </>,
                          <>
                            Configure los niveles de volumen (zonas de
                            concentración: 40–50 dB, zonas de colaboración:
                            60–70 dB, recepción: 55–65 dB).
                          </>,
                          <>
                            Configurez les niveaux sonores (zones de
                            concentration&nbsp;: 40 à 50 dB, espaces de
                            collaboration&nbsp;: 60 à 70 dB, zone
                            d'accueil&nbsp;: 55 à 65 dB)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              볼륨
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              레벨을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설정하세요
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (집중
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              40~50데시벨,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              협업
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              60~70데시벨,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              접수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              55~65데시벨).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Set up zone-specific volume controls</>,
                          <>
                            Configure controles de volumen específicos para cada
                            zona.
                          </>,
                          <>
                            Configurez des commandes de volume spécifiques à
                            chaque zone.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              구역별
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조절
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기능을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설정하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Create clear visual signage for each zone</>,
                          <>Cree señalización visual clara para cada zona.</>,
                          <>
                            Créez une signalisation visuelle claire pour chaque
                            zone.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              각
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              명확한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시각적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              표지판을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설치하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'week-3-soft-launch-and-testing'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Week 3: Soft Launch and Testing
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Semana 3: Lanzamiento preliminar y pruebas
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Semaine 3&nbsp;: Lancement progressif et tests
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          3주차:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소프트
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          런칭
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          테스트
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <section id={'day-15-and-16-pilot-program'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 15–16: Pilot Program
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 15-16: Programa piloto
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jour 15-16&nbsp;: Programme pilote
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            15일차~16일차:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            파일럿
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            프로그램
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Announce music program launch date to all employees
                          </>,
                          <>
                            Anunciar la fecha de lanzamiento del programa de
                            música a todas las empleadas.
                          </>,
                          <>
                            Annoncez la date de lancement du programme musical à
                            tous les employés.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              모든
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              직원에게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              프로그램
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              출시
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              날짜를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공지하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Share guidelines, zone maps, and playlist access
                            links
                          </>,
                          <>
                            Comparta las directrices, los mapas de zonas y los
                            enlaces de acceso a las listas de reproducción.
                          </>,
                          <>
                            Partagez les consignes, les cartes des zones et les
                            liens d'accès aux listes de lecture.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              가이드라인,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              및
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재생
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              접속
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              링크를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공유하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Explain the neuroscience rationale and expected
                            benefits
                          </>,
                          <>
                            Explique la justificación neurocientífica y los
                            beneficios esperados.
                          </>,
                          <>
                            Expliquez la justification neuroscientifique et les
                            bénéfices attendus.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              신경과학적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              근거와
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              예상되는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              이점을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설명하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Emphasize trial period with opportunities for
                            feedback
                          </>,
                          <>
                            Haga hincapié en el período de prueba y en las
                            oportunidades para recibir comentarios.
                          </>,
                          <>
                            Mettez l'accent sur la période d'essai et les
                            possibilités de donner son avis.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              체험
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기간을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              강조하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제공할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기회를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제공하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'day-17-to-21-initial-implementation'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 17–21: Initial Implementation
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 17-21: Implementación inicial
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jours 17 à 21&nbsp;: Mise en œuvre initiale
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            17일차~21일차:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            초기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            구현
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Launch music program in designated zones</>,
                          <>
                            Lanzar el programa de música en las zonas
                            designadas.
                          </>,
                          <>
                            Lancer le programme musical dans les zones
                            désignées.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              지정된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              프로그램을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              시작합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Monitor employee reactions and usage patterns</>,
                          <>
                            Supervise las reacciones y los patrones de uso de
                            los empleados.
                          </>,
                          <>
                            Surveiller les réactions et les habitudes
                            d'utilisation des employés.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              직원들의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              반응과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              패턴을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              모니터링하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Make quick volume adjustments based on immediate
                            feedback
                          </>,
                          <>
                            Realice ajustes rápidos de volumen basándose en la
                            retroalimentación inmediata.
                          </>,
                          <>
                            Effectuez des ajustements rapides du volume en
                            fonction des retours immédiats.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              즉각적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              바탕으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              빠르게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조절하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Schedule daily check-ins with zone representatives
                          </>,
                          <>
                            Programa reuniones diarias de seguimiento con los
                            representantes de cada zona.
                          </>,
                          <>
                            Planifiez des points de contact quotidiens avec les
                            représentants de chaque zone.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              각
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지역
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              담당자들과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              매일
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              정기적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              점검
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              회의를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              계획하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Set up feedback mechanism (Slack channel, suggestion
                            box, quick survey)
                          </>,
                          <>
                            Establecer un mecanismo de retroalimentación (canal
                            de Slack, buzón de sugerencias, encuesta rápida).
                          </>,
                          <>
                            Mettre en place un mécanisme de feedback (canal
                            Slack, boîte à suggestions, sondage rapide)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              피드백
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              메커니즘을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구축하세요
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (슬랙
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              채널,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              건의함,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              간단한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설문조사
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              등).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'week-4-feedback-collection'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Week 4: Feedback Collection and First Iteration
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Semana 4: Recopilación de comentarios y primera
                          iteración
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Semaine 4&nbsp;: Collecte des retours et première
                          itération
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          4주차:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          피드백
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수집
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          첫
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          번째
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          반복
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <section id={'days-22-to-25-more-detailed-feedback'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 22–25: Gather Detailed Feedback
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 22-25: Recopilar comentarios detallados
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jour 22 à 25&nbsp;: Recueillir des commentaires
                            détaillés
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            22일~25일차:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            상세한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수집
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Conduct brief pulse survey (5 questions maximum) on
                            initial experience
                          </>,
                          <>
                            Realice una breve encuesta (máximo 5 preguntas)
                            sobre la experiencia inicial.
                          </>,
                          <>
                            Réaliser une courte enquête éclair (5 questions
                            maximum) sur l'expérience initiale.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              초기
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경험에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              간단한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설문조사(최대
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              5개
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              질문)를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              실시합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Host drop-in feedback sessions where employees can
                            share thoughts
                          </>,
                          <>
                            Organice sesiones informales de retroalimentación
                            donde los empleados puedan compartir sus opiniones.
                          </>,
                          <>
                            Organisez des séances de feedback informelles où les
                            employés peuvent partager leurs opinions.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              직원들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              자유롭게
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              의견을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공유할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              비공식적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              세션을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개최하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Observe actual space usage: Are zones being used as
                            intended?
                          </>,
                          <>
                            Observe el uso real del espacio: ¿Se están
                            utilizando las zonas según lo previsto?
                          </>,
                          <>
                            Observez l'utilisation réelle de l'espace&nbsp;: les
                            zones sont-elles utilisées conformément à leur
                            destination&nbsp;?
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              실제
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공간
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              현황을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관찰하세요.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역들이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              의도한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용되고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니까?
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Note any technical issues or volume complaints</>,
                          <>
                            Anote cualquier problema técnico o queja relacionada
                            con el volumen.
                          </>,
                          <>
                            Notez tout problème technique ou toute plainte
                            concernant le volume sonore.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              기술적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              문제나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              음량
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관련
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              불만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사항이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있으면
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              알려주세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'days-26-to-28-optimization-round'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Day 26–28: First Optimization Round
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Días 26-28: Primera ronda de optimización
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Jours 26 à 28&nbsp;: Première phase d'optimisation
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            26일~28일:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            첫
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            번째
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            최적화
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            단계
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Analyze Week 3 feedback for actionable patterns</>,
                          <>
                            Analice los comentarios de la semana 3 para
                            identificar patrones que permitan tomar medidas
                            concretas.
                          </>,
                          <>
                            Analysez les commentaires de la semaine 3 afin
                            d'identifier des tendances exploitables.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              3주차
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분석하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              실행
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              패턴을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              파악하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Adjust playlist selections based on genre
                            preferences
                          </>,
                          <>
                            Ajusta las selecciones de la lista de reproducción
                            según tus preferencias de género.
                          </>,
                          <>
                            Ajustez les sélections de la liste de lecture en
                            fonction de vos préférences musicales.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              장르
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선호도에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재생
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목록
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              선택을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조정하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Fine-tune volume levels in zones receiving
                            complaints
                          </>,
                          <>
                            Ajuste con precisión los niveles de volumen en las
                            zonas donde se han recibido quejas.
                          </>,
                          <>
                            Ajustez précisément les niveaux de volume dans les
                            zones où des plaintes ont été signalées.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              민원이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              접수된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              볼륨
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              레벨을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              미세
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조정하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Add or remove tracks causing repeated negative
                            feedback
                          </>,
                          <>
                            Añade o elimina las pistas que generan comentarios
                            negativos de forma recurrente.
                          </>,
                          <>
                            Ajoutez ou supprimez les morceaux qui génèrent des
                            commentaires négatifs à répétition.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              반복적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부정적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              유발하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              트랙을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              추가하거나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제거하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Modify zone boundaries if needed based on usage
                            observations
                          </>,
                          <>
                            Modifique los límites de las zonas si es necesario,
                            basándose en las observaciones de uso.
                          </>,
                          <>
                            Modifiez les limites des zones si nécessaire en
                            fonction des observations relatives à leur
                            utilisation.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              사용량
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관찰
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              결과를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              바탕으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              필요에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경계를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수정하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'months-2-to-6-optimizations'}>
                  <h4 className={classes.subSection}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Ongoing: Month 2–6 Optimization
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          En curso: Optimización (meses 2 a 6)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          En cours&nbsp;: Optimisation des mois 2 à 6
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          진행
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          중:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          2~6개월차
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최적화
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작업
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <section id={'monthly-actions'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Monthly Actions:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Acciones mensuales:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Actions mensuelles&nbsp;:
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            월별
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활동:
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Conduct brief satisfaction survey (1-minute, 3–5
                            questions)
                          </>,
                          <>
                            Realizar una breve encuesta de satisfacción (1
                            minuto, 3-5 preguntas). Acciones mensuales:
                          </>,
                          <>
                            Réaliser une courte enquête de satisfaction (1
                            minute, 3 à 5 questions)
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              간단한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              만족도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              설문조사를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              실시합니다
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              (1분
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소요,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              3~5개
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              질문).
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Rotate playlists to maintain freshness</>,
                          <>
                            Rota las listas de reproducción para mantenerlas
                            actualizadas.
                          </>,
                          <>
                            Alternez les listes de lecture pour maintenir la
                            fraîcheur du contenu.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              플레이리스트를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              주기적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              변경하여
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              신선함을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              유지하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Review productivity metrics if available</>,
                          <>
                            Revise las métricas de productividad si están
                            disponibles.
                          </>,
                          <>
                            Examinez les indicateurs de productivité, s'ils sont
                            disponibles.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경우
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              생산성
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지표를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              검토하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Host quarterly town halls to discuss music program
                          </>,
                          <>
                            Organizar reuniones trimestrales con la comunidad
                            para debatir sobre el programa de música.
                          </>,
                          <>
                            Organiser des réunions publiques trimestrielles pour
                            discuter du programme musical.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              프로그램에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              논의하기
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위해
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분기별로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              타운홀
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              미팅을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              개최합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                  <section id={'quarterly-actions'}>
                    <h5 className={classes.pParagraph}>
                      {translate(
                        <>
                          <span className={classes.spanBolder}>
                            Quarterly Actions:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Acciones trimestrales:
                          </span>
                        </>,
                        <>
                          <span className={classes.spanBolder}>
                            Actions trimestrielles&nbsp;:
                          </span>
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            분기별
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조치
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사항:
                          </span>{' '}
                        </>,
                        selectedLanguage
                      )}
                    </h5>
                    <ul
                      className={
                        classes.standardText + ' ' + classes.ulArticles
                      }
                    >
                      <li className={classes.liArticle}>
                        {translate(
                          <>Comprehensive program review with stakeholders</>,
                          <>
                            Revisión exhaustiva del programa con las partes
                            interesadas.
                          </>,
                          <>
                            Examen complet du programme avec les parties
                            prenantes.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              이해관계자들과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              함께하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              종합적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              프로그램
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              검토
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Analyze retention, absenteeism, and stress metrics
                          </>,
                          <>
                            Analizar las métricas de retención, absentismo y
                            estrés.
                          </>,
                          <>
                            Analyser les indicateurs de rétention, d'absentéisme
                            et de stress.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              직원
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              유지율,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              결근율
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              및
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              스트레스
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지표를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              분석합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>Update playlists for seasonal variation</>,
                          <>
                            Actualizar las listas de reproducción según las
                            variaciones estacionales.
                          </>,
                          <>
                            Mettre à jour les playlists en fonction des
                            variations saisonnières
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              계절
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              변화에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              맞춰
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재생
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목록을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업데이트하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Reassess zone configurations based on evolving work
                            patterns
                          </>,
                          <>
                            Reevaluar las configuraciones de las zonas en
                            función de la evolución de los patrones de trabajo.
                          </>,
                          <>
                            Réévaluer la configuration des zones en fonction de
                            l'évolution des modes de travail.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              변화하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              업무
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              패턴에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구역
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구성을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              재평가하십시오.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                      <li className={classes.liArticle}>
                        {translate(
                          <>
                            Celebrate wins and share positive feedback with the
                            organization
                          </>,
                          <>
                            Celebre los éxitos y comparta comentarios positivos
                            con la organización.
                          </>,
                          <>
                            Célébrez les succès et partagez les retours positifs
                            avec l'organisation.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              성공
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사례를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              축하하고
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              긍정적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              피드백을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              조직
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              전체와
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공유하세요.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </li>
                    </ul>
                  </section>
                </section>
                <section id={'competitive-advantages-of-strategic-sound'}>
                  <h3 className={classes.subSection}>
                    {translate(
                      <>The Competitive Advantage of Strategic Sound Design</>,
                      <>
                        La ventaja competitiva del diseño de sonido estratégico
                      </>,
                      <>
                        L'avantage concurrentiel d'une conception sonore
                        stratégique
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          전략적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사운드
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          디자인의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경쟁
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          우위
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </h3>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        You now have everything needed to transform your
                        workplace through strategic music implementation: the
                        neuroscience proving why it works from Part 1, practical
                        strategies for every work environment, a clear 4-step
                        framework, common mistakes to avoid, and a week-by-week
                        action plan.
                      </>,
                      <>
                        Ahora dispone de todo lo necesario para transformar su
                        lugar de trabajo mediante la implementación estratégica
                        de música: los estudios de neurociencia que demuestran
                        por qué funciona (presentados en la Parte 1),
                        estrategias prácticas para cada entorno laboral, un
                        marco de trabajo claro de 4 pasos, los errores comunes
                        que debe evitar y un plan de acción semanal.
                      </>,
                      <>
                        Maintenant, je dispose de tout ce qu'il faut pour
                        transformer votre lieu de travail au cours de la mise en
                        œuvre stratégique de la musique&nbsp;: los estudios de
                        neurociencia que demuestran por qué funciona (présentés
                        dans la partie 1), stratégies pratiques pour chaque
                        personne entorno laboral, un marco de trabajo claro de 4
                        pasos, los Des erreurs communes à éviter et un plan
                        d'action sémanal.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이제
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활용을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직장을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          변화시키는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          데
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          갖추셨습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          1부에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다룬
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이유를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          뒷받침하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          신경과학적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          근거,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          적용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실질적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          명확한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          4단계
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프레임워크,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          피해야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          일반적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실수,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그리고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          주별
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실행
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          계획까지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모두
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제공됩니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        The business case is compelling. Music reduces stress by
                        up to 61%, boosts performance by 90%, improves
                        retention, enhances collaboration, and elevates employee
                        satisfaction&nbsp;— all through measurable physiological
                        and psychological mechanisms.
                      </>,
                      <>
                        Los argumentos a favor son convincentes. La música
                        reduce el estrés hasta en un 61%, aumenta el rendimiento
                        en un 90%, mejora la retención de empleados, fomenta la
                        colaboración y eleva la satisfacción laboral, todo ello
                        a través de mecanismos fisiológicos y psicológicos
                        medibles.
                      </>,
                      <>
                        Les arguments en faveur de cette approche sont
                        convaincants. La musique réduit le stress jusqu'à
                        61&nbsp;%, améliore les performances de 90&nbsp;%,
                        favorise la fidélisation des employés, renforce la
                        collaboration et accroît la satisfaction au
                        travail&nbsp;– le tout grâce à des mécanismes
                        physiologiques et psychologiques mesurables.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          주는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이점은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매우
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          설득력
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스트레스를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최대
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          61%까지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          줄여주고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          업무
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효율성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          90%
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          향상시키며,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유지율을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          높이고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          협업을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          강화하고,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직원
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          만족도를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          높여줍니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          측정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생리적,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          심리적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          메커니즘을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이루어집니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        Smart companies recognize that office background
                        playlists, productivity soundscapes, and thoughtful
                        sound design for offices aren’t luxuries&nbsp;— they’re
                        competitive advantages in the war for talent and the
                        pursuit of optimal performance.
                      </>,
                      <>
                        Las empresas inteligentes reconocen que las listas de
                        reproducción de música ambiental para oficinas, los
                        ambientes sonoros que favorecen la productividad y un
                        diseño de sonido cuidado para los espacios de trabajo no
                        son lujos, sino ventajas competitivas en la lucha por el
                        talento y en la búsqueda del rendimiento óptimo.
                      </>,
                      <>
                        Les entreprises avisées comprennent que les listes de
                        lecture musicales d'ambiance, les ambiances sonores
                        propices à la productivité et la conception sonore
                        soignée des bureaux ne sont pas des luxes, mais de
                        véritables atouts concurrentiels dans la guerre des
                        talents et la recherche de performances optimales.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          스마트한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기업들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사무실
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          배경
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          재생
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          목록,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          생산성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          높이는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사운드
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          환경,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그리고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사무실을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          세심한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사운드
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          디자인이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사치가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아니라
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인재
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          확보
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경쟁과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최적의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          성과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          달성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경쟁
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          우위
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요소라는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인식하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        The question isn’t whether strategic workplace music
                        works. The science settled that question decisively.
                      </>,
                      <>
                        La cuestión no es si la música estratégica en el lugar
                        de trabajo funciona. La ciencia ya ha resuelto esa
                        cuestión de forma concluyente.
                      </>,
                      <>
                        La question n'est pas de savoir si la musique
                        stratégique au travail est efficace. La science a
                        tranché cette question de manière décisive.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전략적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          직장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          효과가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          없는지가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          아닙니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          과학적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이미
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          질문에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          답은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          명확하게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          나왔습니다.
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                  <p className={classes.pParagraph}>
                    {translate(
                      <>
                        The only remaining question: Will you implement this
                        advantage before your competitors do?
                      </>,
                      <>
                        La única pregunta que queda es: ¿Implementarás esta
                        ventaja antes que tus competidores?
                      </>,
                      <>
                        La seule question qui reste&nbsp;: allez-vous exploiter
                        cet avantage avant vos concurrents&nbsp;?
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이제
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          남은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          유일한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          질문은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이것입니다.
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          당신은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경쟁사보다
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          먼저
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이점을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          활용할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있을까요?
                        </span>{' '}
                      </>,
                      selectedLanguage
                    )}
                  </p>
                </section>
              </section>
            </section>
            <section id={'continue-the-conversation-with-us'}>
              <h3 className={classes.subSection}>
                {translate(
                  <>Continue the Conversation</>,
                  <>Continuar la conversación</>,
                  <>Poursuivre la conversation</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대화를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      계속하세요
                    </span>{' '}
                  </>,
                  selectedLanguage
                )}
              </h3>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Your experience matters. Have you implemented workplace
                    music in your organization? What worked? What surprised you?
                    What challenges did you face? Questions about specific
                    scenarios?
                  </>,
                  <>
                    Tu experiencia es importante. ¿Has implementado música en el
                    lugar de trabajo en tu organización? ¿Qué funcionó? ¿Qué te
                    sorprendió? ¿Qué desafíos enfrentaste? ¿Tienes preguntas
                    sobre situaciones específicas?
                  </>,
                  <>
                    Votre expérience nous intéresse. Avez-vous mis en place la
                    diffusion de musique sur votre lieu de travail&nbsp;?
                    Qu'est-ce qui a fonctionné&nbsp;? Qu'est-ce qui vous a
                    surpris&nbsp;? Quels défis avez-vous rencontrés&nbsp;?
                    Avez-vous des questions concernant des situations
                    spécifiques&nbsp;?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여러분의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경험은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소중합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      조직에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      직장
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      도입해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보셨나요?
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떤
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      점이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      효과적이었나요?
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떤
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      점이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      놀라웠나요?
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떤
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어려움에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      직면하셨나요?
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      특정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      상황에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      질문이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있으신가요?
                    </span>{' '}
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Our team monitors comments and guides unique workplace
                    challenges. If you want to contact us directly, visit
                    now&nbsp;—{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST}
                      title={
                        'Get in touch for an expert consultation on how background music can improve your workplace or place of business.'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                  </>,
                  <>
                    Nuestro equipo supervisa los comentarios y brinda
                    orientación sobre los desafíos específicos del entorno
                    laboral. Si desea contactarnos directamente, visite nuestra
                    página ahora mismo&nbsp;—{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_ES}
                      title={
                        'Contáctenos para obtener una consulta experta sobre cómo la música de fondo puede mejorar su lugar de trabajo o negocio.'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                  </>,
                  <>
                    Notre équipe surveille les commentaires et vous accompagne
                    face aux défis spécifiques du monde du travail. Si vous
                    souhaitez nous contacter directement, rendez-vous sur notre
                    site dès maintenant&nbsp;—{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_FR}
                      title={
                        "Contactez-nous pour une consultation d'experts sur la manière dont la musique d'ambiance peut améliorer votre lieu de travail ou votre commerce."
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저희
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      팀은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여러분의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      의견을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경청하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      직장에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      발생하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다양한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      문제
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해결을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      도와드립니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      직접
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      문의하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      싶으시면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지금
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다음
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      링크를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방문하세요:
                    </span>{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={MAIN_SITE_ENVIRONMENT_HOST_KO}
                      title={
                        '배경 음악이 직장이나 사업장에 어떤 긍정적인 영향을 미칠 수 있는지 전문가의 상담을 받아보세요. 지금 바로 문의하세요.'
                      }
                      rel="noopener noreferrer"
                    >
                      <span translate="no">
                        Practical&nbsp;
                        <span className={classes.spanPre}>
                          Stream&nbsp;
                          <IconExternalLink />
                        </span>
                      </span>
                    </a>{' '}
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={false}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
